import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Upload,
  Download,
  CheckCircle,
  AlertTriangle,
  FileText,
  Users,
  DollarSign,
  Calendar,
  X,
  Save,
  Loader,
  Settings,
  Clock,
  Eye,
  XCircle,
  Edit,
  FileDown
} from 'lucide-react';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api, { formatCurrency, formatDate, subscriptionAPI, allocationRequestsAPI, locApprovalAPI, filesAPI, jobsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useAllocation } from '../../contexts/AllocationContext';

const LOCManagement = () => {
  // navigate removed with View Allocations button
  const { addAllocation, hasBondAllocation } = useAllocation();
  const [selectedBond, setSelectedBond] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [allocations, setAllocations] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [csvErrors, setCsvErrors] = useState([]);
  const [csvWarnings, setCsvWarnings] = useState([]);
  const [csvStats, setCsvStats] = useState(null);
  const [approvalStates, setApprovalStates] = useState({}); // bondId -> state
  // Unified notification state (replaces window.alert)
  const [notice, setNotice] = useState({ open: false, type: 'info', title: '', message: '' });
  const showNotice = (type, title, message) => setNotice({ open: true, type, title, message });
  const closeNotice = () => setNotice(n => ({ ...n, open: false }));

  // Auto-dismiss notification after 3 seconds
  useEffect(() => {
    if (notice.open) {
      const timer = setTimeout(() => {
        closeNotice();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notice.open]);
  // LOC Management modal states (removed - now showing inline)
  // const [showLocModal, setShowLocModal] = useState(false);
  // const [showSubmissionDetailsModal, setShowSubmissionDetailsModal] = useState(false);
  // const [locData, setLocData] = useState(null);
  // const [selectedInvestor, setSelectedInvestor] = useState(null);
  // const [locLoading, setLocLoading] = useState(false);
  const [editingSubmissionId, setEditingSubmissionId] = useState(null);
  const [editingRequestId, setEditingRequestId] = useState(null);
  const [editingAmount, setEditingAmount] = useState('');
  const [originalEditAmount, setOriginalEditAmount] = useState('');
  const [submissionSaving, setSubmissionSaving] = useState(false);
  const [approvingBonds, setApprovingBonds] = useState(new Set()); // Set of bondIds currently being approved
  const [rejectingBonds, setRejectingBonds] = useState(new Set()); // Set of bondIds currently being rejected
  const [approvingRequests, setApprovingRequests] = useState(new Set()); // Set of requestIds currently being approved in modal
  const [locApprovalStates, setLocApprovalStates] = useState({}); // allocationRequestId -> allocation request with embedded LOC approval fields
  const [bondLocSummary, setBondLocSummary] = useState({}); // bondId -> { directInvestors: [], custodians: [] }
  const [activeJobs, setActiveJobs] = useState({}); // jobId -> job data for polling
  const [jobPollingIntervals, setJobPollingIntervals] = useState({}); // jobId -> intervalId for cleanup
  const [refreshTimeout, setRefreshTimeout] = useState(null); // Debounce timeout for refreshLocData
  const [lastFetchTime, setLastFetchTime] = useState(0); // Track last LOC data fetch time
  const CACHE_DURATION = 2000; // Cache for 2 seconds (reduced for faster updates)
  const isMountedRef = useRef(true); // Track if component is mounted
  const isPageVisibleRef = useRef(true); // Track if page is visible

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    onConfirm: null,
    showInput: false,
    inputLabel: '',
    inputPlaceholder: '',
    inputValue: '',
    isTextArea: false
  });

  const { user: authUser } = useAuth() || {};
  const userRole = authUser?.role;
  const isFacilityAgent = userRole === 'facility_agent';
  const allocationEntityLabel = isFacilityAgent ? 'Primary Subscriber' : (userRole === 'primary_subscriber' ? 'Recipient' : 'Investor');
  const allocationEntityPluralLabel = isFacilityAgent ? 'Primary Subscribers' : (userRole === 'primary_subscriber' ? 'Recipients' : 'Investors');
  const allocationEntityDescription = isFacilityAgent ? 'primary subscribers' : (userRole === 'primary_subscriber' ? 'custodians or direct investors' : 'investors');
  const allocationEntityIdLabel = `${allocationEntityLabel} ID`;
  const allocationEntityNameLabel = `${allocationEntityLabel} Name`;
  const uploadActionsEnabled = false;

  // Load bonds from API that are ready for allocation
  const [allocationBonds, setAllocationBonds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filter state
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchAllocationBonds = async (page = currentPage, limit = pageSize, status = statusFilter) => {
    try {
      setLoading(true);
      setError(null);

      // Get LOC approvals grouped by bond with pagination from backend
      const response = await locApprovalAPI.getMyApprovals(page, limit, status);
      console.log('LOC approvals:', response.data);

      const result = response?.data?.data || {};
      const approvals = result.data || [];
      const pagination = result.pagination || {};

      // Extract unique bonds from LOC approvals and aggregate stats
      const bondMap = new Map();
      approvals.forEach((approval) => {
        const bond = approval.bondId;
        if (!bond || !bond._id) return;

        const bondId = bond._id;
        if (!bondMap.has(bondId)) {
          bondMap.set(bondId, {
            ...bond,
            id: bondId,
            stockCode: bond.stockCode,
            subscriptions: 0,
            totalSubscribed: 0,
            nominalAmount: bond.nominalAmount || 0,
            productType: bond.productType || (bond.bondType === 'Islamic' ? 'Sukuk' :
              bond.bondType === 'Conventional' ? 'Conventional Bonds' : bond.bondType || 'Bond'),
            status: bond.status || 'Pending Review'
          });
        }

        // Aggregate stats from LOC approvals
        const bondData = bondMap.get(bondId);
        bondData.subscriptions += 1; // Count unique LOC approvals
        bondData.totalSubscribed += approval.totalRequestedAmount || 0;
      });

      const allBonds = Array.from(bondMap.values()).map(bond => ({
        ...bond,
        subscriptionRatio: bond.nominalAmount && bond.totalSubscribed ?
          Number((bond.totalSubscribed / bond.nominalAmount).toFixed(2)) : 1.0
      }));

      // Use backend pagination values
      setTotalRecords(pagination.total || 0);
      setTotalPages(pagination.totalPages || 0);
      setCurrentPage(pagination.page || page);
      setAllocationBonds(allBonds);
    } catch (error) {
      console.error('Error fetching LOC approvals:', error);
      // Don't set error state for auth errors to prevent showing error message while redirecting
      if (error.response?.status !== 401) {
        setError(error.response?.data?.message || error.message || 'Failed to load LOC approvals');
      }
      setAllocationBonds([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch bonds on component mount and when pagination or filter changes
  React.useEffect(() => {
    fetchAllocationBonds(currentPage, pageSize, statusFilter);
  }, [currentPage, pageSize, statusFilter]);

  // Load LOC data for all bonds from locApproval table
  React.useEffect(() => {
    const loadLocData = async () => {
      if (allocationBonds.length === 0) return;

      try {
        const bondIds = allocationBonds
          .map(bond => bond.id)
          .filter(id => id && id !== 'null' && id !== null);

        if (bondIds.length === 0) return;

        // Fetch LOC approvals for each bond from the locApproval table
        const locApprovalPromises = bondIds.map(bondId =>
          locApprovalAPI.getApprovalsByBond(bondId).catch(err => {
            console.error(`Error fetching LOC approvals for bond ${bondId}:`, err);
            return { data: { success: false, data: [] } };
          })
        );

        const locApprovalResponses = await Promise.all(locApprovalPromises);

        // Build bondLocSummary from LOC approvals
        const bondDataMap = {};
        const locApprovalMap = {};

        bondIds.forEach((bondId, index) => {
          const response = locApprovalResponses[index];
          if (!response.data.success) {
            bondDataMap[bondId] = { directInvestors: [], custodians: [] };
            return;
          }

          const locApprovals = response.data.data || [];
          const directInvestors = [];
          const custodiansMap = {};

          locApprovals.forEach(approval => {
            // Store LOC approval in map by ID
            locApprovalMap[approval._id] = approval;

            if (approval.custodianId) {
              // Sub-investor: group by custodian
              const custodianId = approval.custodianId._id || approval.custodianId;
              if (!custodiansMap[custodianId]) {
                custodiansMap[custodianId] = {
                  id: custodianId,
                  name: approval.custodianId.corporateInfo?.companyName ||
                    approval.custodianId.corporateInfo?.legalEntityName ||
                    'Unknown Custodian',
                  locApprovals: [],
                  submissions: [],
                  totalAmount: 0
                };
              }
              custodiansMap[custodianId].locApprovals.push(approval);
              // Add formatted submission for UI display
              custodiansMap[custodianId].submissions.push({
                _id: approval._id,
                id: approval._id,
                requestId: approval._id,
                investorName: approval.investorId?.corporateInfo?.companyName ||
                  approval.investorId?.corporateInfo?.legalEntityName ||
                  approval.investorId?.personalInfo?.fullName ||
                  [approval.investorId?.personalInfo?.firstName, approval.investorId?.personalInfo?.lastName]
                    .filter(Boolean)
                    .join(' ') ||
                  'Unknown Sub-Investor',
                investorEmail: approval.investorId?.email || '',
                amount: approval.totalRequestedAmount || 0,
                locApproval: approval
              });
              custodiansMap[custodianId].totalAmount += approval.totalRequestedAmount || 0;
            } else {
              // Direct investor
              directInvestors.push({
                id: approval._id, // Use LOC approval ID
                investorId: approval.investorId._id || approval.investorId,
                name: approval.investorId.corporateInfo?.companyName ||
                  approval.investorId.corporateInfo?.legalEntityName ||
                  approval.investorId.personalInfo?.fullName ||
                  [approval.investorId.personalInfo?.firstName, approval.investorId.personalInfo?.lastName]
                    .filter(Boolean)
                    .join(' ') ||
                  'Unknown Investor',
                email: approval.investorId.email || '',
                totalAmount: approval.totalRequestedAmount || 0,
                locApproval: approval
              });
            }
          });

          bondDataMap[bondId] = {
            directInvestors,
            custodians: Object.values(custodiansMap)
          };
        });

        setBondLocSummary(bondDataMap);
        setLocApprovalStates(locApprovalMap);

        // Resume polling for any active jobs (handles page refresh during job processing)
        resumeActiveJobPolling(locApprovalMap);

      } catch (error) {
        console.error('Error loading LOC data:', error);
      }
    };

    if (allocationBonds.length > 0) {
      loadLocData();
    }
  }, [allocationBonds]);

  // Load approval state for each bond
  useEffect(() => {
    const loadStates = async () => {
      if (allocationBonds.length === 0) return;

      try {
        // Batch fetch all allocation approvals in one API call
        const bondIds = allocationBonds
          .map(b => b.id)
          .filter(id => id && id !== 'null' && id !== null); // Filter out null/invalid IDs

        if (bondIds.length === 0) {
          return; // No valid bond IDs to fetch
        }

        const res = await subscriptionAPI.getAllocationApprovals(bondIds);
        if (res.data.success) {
          setApprovalStates(res.data.data);
        }
      } catch (e) {
        console.error('Error loading allocation approval states:', e);
      }
    };
    if (allocationBonds.length) loadStates();
  }, [allocationBonds]);

  // Mock data for demonstration (keeping some existing bonds with realistic data)
  const mockFinalizedBonds = [
    {
      id: 999,
      bondName: 'Sukuk Khazanah Demo 2025',
      issuer: 'Solar Power Malaysia',
      productType: 'Sukuk',
      nominalAmount: 200000000,
      issueDate: '2025-07-22',
      maturityDate: '2028-12-25',
      profitRate: 5.50,
      status: 'Allocation Uploaded',
      subscriptions: 12, // Real number of subscription applications
      totalSubscribed: 220000000, // Real total amount subscribed
      subscriptionRatio: 1.1 // Real oversubscription ratio (220M/200M = 1.1x)
    }
  ];

  // Function to get bonds from Subscription Management (placeholder)
  const getFinalizeBondsFromSubscriptionManagement = () => {
    // This function returns bonds that have completed subscription periods
    // For now, return empty array - bonds are loaded from API
    return [];
  };

  // Use allocation bonds from API (filtered by backend)
  const filteredBonds = useMemo(() => {
    return allocationBonds;
  }, [allocationBonds]);

  const validateAllocationsWithDB = async (parsedAllocations) => {
    try {
      const response = await subscriptionAPI.validateAllocationData(parsedAllocations);

      if (response.data.success) {
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Validation failed');
      }
    } catch (error) {
      console.error('Database validation error:', error);
      throw new Error(`Database validation failed: ${error.message}`);
    }
  };

  // CSV parsing function (aligned with custodian page style; provides rowNumber & blocking errors)
  const parseCSV = (csvText) => {
    const lines = csvText.trim().split('\n');
    if (!lines.length) {
      return { data: [], errors: ['Empty CSV file'], stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 } };
    }
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const normalizedHeaders = headers.map(h => h.toLowerCase().trim());
    const flexibleHeaderMapping = {
      'investor id': 'investorId', 'investorid': 'investorId', 'id': 'investorId',
      'investor name': 'investorName', 'investorname': 'investorName', 'name': 'investorName',
      'primary subscriber id': 'investorId', 'primarysubscriber id': 'investorId', 'primarysubscriberid': 'investorId',
      'primary subscriber name': 'investorName', 'primarysubscriber name': 'investorName', 'primarysubscribername': 'investorName',
      'recipient id': 'investorId', 'recipientid': 'investorId',
      'recipient name': 'investorName', 'recipientname': 'investorName',
      'token amount': 'tokenAmount', 'tokenamount': 'tokenAmount', 'amount': 'tokenAmount', 'allocation': 'tokenAmount',
      'lei': 'lei', 'legal entity identifier': 'lei', 'legalentityidentifier': 'lei'
    };
    const headerIndexMap = {};
    normalizedHeaders.forEach((h, idx) => {
      const field = flexibleHeaderMapping[h];
      if (field && headerIndexMap[field] === undefined) headerIndexMap[field] = idx;
    });
    const requiredFields = ['investorId', 'investorName', 'tokenAmount', 'lei'];
    const missing = requiredFields.filter(f => headerIndexMap[f] === undefined);
    if (missing.length) {
      return {
        data: [],
        errors: [`Missing required columns. Please ensure your CSV has columns for: ${missing.join(', ')}`],
        stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 }
      };
    }
    const data = []; const errors = []; let totalAmount = 0;
    for (let i = 1; i < lines.length; i++) {
      const rawLine = lines[i];
      const currentLine = rawLine.trim();
      if (!currentLine) continue; // skip empty
      if (currentLine.startsWith('#') || currentLine.startsWith('//')) continue; // skip comments
      const values = currentLine.split(',').map(v => v.trim().replace(/"/g, ''));
      const row = {}; requiredFields.forEach(f => { row[f] = values[headerIndexMap[f]] || ''; });
      row.rowNumber = i + 1; // header is line 1
      // Skip if all empty
      if (requiredFields.every(f => row[f] === '')) continue;
      const rowErrors = [];
      // Participant ID / Name validations (role-based labeling)
      if (!row.investorId || row.investorId.length < 3) rowErrors.push(`Row ${row.rowNumber}: Invalid ${allocationEntityIdLabel} "${row.investorId}"`);
      if (!row.investorName || row.investorName.length < 2) rowErrors.push(`Row ${row.rowNumber}: Invalid ${allocationEntityNameLabel} "${row.investorName}"`);
      // Token Amount
      let tokenAmount; try { tokenAmount = parseFloat(String(row.tokenAmount).replace(/,/g, '')); } catch { tokenAmount = NaN; }
      if (isNaN(tokenAmount) || tokenAmount <= 0) rowErrors.push(`Row ${row.rowNumber}: Invalid Bond Amount "${row.tokenAmount}"`); else row.tokenAmount = tokenAmount;
      // LEI validation (basic length check - standard LEI is 20 characters)
      if (!row.lei || row.lei.length !== 20) rowErrors.push(`Row ${row.rowNumber}: Invalid LEI "${row.lei}" (must be exactly 20 characters)`);
      else row.lei = row.lei.trim().toUpperCase();
      if (rowErrors.length) errors.push(...rowErrors); else { data.push(row); totalAmount += row.tokenAmount; }
    }
    if (!data.length) errors.push('No valid data rows found in CSV file');
    return { data, errors, stats: { totalRows: lines.length - 1, validRows: data.length, totalAmount, totalPercentage: 0 } };
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.csv')) { showNotice('error', 'Invalid File Type', 'Please upload a CSV file only.'); return; }
    if (file.size > 5 * 1024 * 1024) { showNotice('error', 'File Too Large', 'File size must be less than 5MB.'); return; }
    setUploadedFile(file); setIsUploading(true); setUploadProgress(0); setCsvErrors([]); setCsvWarnings([]); setCsvStats(null);
    try {
      // Simulated progress (UI consistency)
      const progressInterval = setInterval(() => setUploadProgress(p => (p >= 90 ? (clearInterval(progressInterval), 90) : p + 10)), 100);
      const text = await new Promise((resolve, reject) => { const r = new FileReader(); r.onload = e => resolve(e.target.result); r.onerror = () => reject(new Error('Failed to read file')); r.readAsText(file); });
      let result; try { result = parseCSV(text); } catch (e) { result = { data: [], errors: [`CSV parsing failed: ${e.message}`], stats: { totalRows: 0, validRows: 0, totalAmount: 0, totalPercentage: 0 } }; }
      clearInterval(progressInterval); setUploadProgress(90);
      // If structural errors / no data -> block
      if (result.errors.length && !result.data.length) { setCsvErrors(result.errors); setAllocations([]); setShowPreview(false); setUploadProgress(100); return; }
      // Server-side validation
      try {
        setUploadProgress(95);
        const dbValidation = await validateAllocationsWithDB(result.data);
        const globalErrors = dbValidation.globalErrors || [];
        const rowNumberByKey = new Map(result.data.map(r => [(`${(r.investorId || '').toLowerCase()}||${(r.walletAddress || '').toLowerCase()}`), r.rowNumber]));
        const invalidMessages = (dbValidation.invalid || []).flatMap(item => {
          const alloc = item.allocation || item; const key = `${(alloc.investorId || '').toLowerCase()}||${(alloc.walletAddress || '').toLowerCase()}`; const rn = item.rowNumber || alloc.rowNumber || rowNumberByKey.get(key) || '?';
          if (Array.isArray(item.errors) && item.errors.length) return item.errors.map(e => `Row ${rn}: ${e}`);
          return [`Row ${rn}: Invalid entry for investor ${alloc.investorId || 'unknown'}`];
        });
        const warnings = [];
        (dbValidation.valid || []).forEach(v => { if (Array.isArray(v.warnings) && v.warnings.length) { const prefix = `${v.allocation?.investorName || ''} (${v.allocation?.investorId || 'unknown'})`; v.warnings.forEach(w => warnings.push(`${prefix}: ${w}`)); } });
        if (dbValidation.hasDuplicates && (dbValidation.duplicateIds || []).length) globalErrors.push(`Duplicate investor IDs found: ${(dbValidation.duplicateIds || []).join(', ')}`);

        const allErrors = [...(result.errors || []), ...globalErrors, ...invalidMessages];
        setUploadProgress(100);
        if (allErrors.length) { // blocking
          setCsvErrors(allErrors); setCsvWarnings(warnings); setAllocations([]); setCsvStats({ ...result.stats, serverValidated: true, validRows: 0 }); setShowPreview(false); return;
        }
        // success
        setCsvErrors([]); setCsvWarnings(warnings); setAllocations(result.data); setCsvStats({ ...result.stats, serverValidated: true, validRows: dbValidation.summary?.valid || result.stats.validRows }); setShowPreview(true);
      } catch (serverErr) {
        setCsvErrors([serverErr.message || 'Server validation failed']); setCsvWarnings([]); setAllocations([]); setShowPreview(false); setCsvStats(result.stats); setUploadProgress(100); return;
      }
    } catch (err) {
      setCsvErrors([`File processing error: ${err.message}`]); setCsvWarnings([]); setAllocations([]); setShowPreview(false); setUploadProgress(0);
    } finally { setIsUploading(false); }
  };

  const handleUploadConfirm = async () => {
    if (!selectedBond) return;
    const approvalState = approvalStates[selectedBond.id];
    const pendingL2 = approvalState && approvalState.status === 'Pending L2';
    const approved = approvalState && approvalState.status === 'Approved';
    try {
      setIsUploading(true);
      // Stage upload (no approval yet). Previous logic only allowed when approvalState was absent OR status === 'Pending L1'.
      // However backend getState() returns an approvalState with status 'Ready for Allocation' before any staging, causing the
      // original condition to skip staging (making the button appear to do nothing). We broaden the condition to allow staging
      // whenever there is no L1 payload and no L1 approval recorded.
      const canStage = !approvalState || (!approvalState.l1Payload && !approvalState.l1ApprovedBy);
      if (canStage) {
        if (allocations.length === 0) {
          showNotice('warning', 'No Data', 'Upload and validate a CSV before staging.');
          return;
        }
        const csvRows = allocations.map(alloc => [alloc.investorId, alloc.investorName, alloc.tokenAmount, alloc.lei]);
        const headerRow = [allocationEntityIdLabel, allocationEntityNameLabel, 'Bond Amount', 'LEI'];
        const csvContent = [headerRow, ...csvRows]
          .map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
        const csvBlob = new Blob([csvContent], { type: 'text/csv' });
        const formData = new FormData();
        formData.append('csvFile', csvBlob, `allocation_${selectedBond.id}.csv`);
        const res = await subscriptionAPI.stageAllocationUpload(selectedBond.id, formData);
        if (res.data.success) {
          setApprovalStates(s => ({ ...s, [selectedBond.id]: res.data.data }));
          showNotice('success', 'Allocation Staged', 'CSV parsed and staged. Re-open to Approve L1.');
          // Close modal & reset transient upload state after staging
          setShowUploadModal(false);
          setShowPreview(false);
          setSelectedBond(null);
          setUploadedFile(null);
          setAllocations([]);
          setCsvErrors([]);
          setCsvWarnings([]);
          setCsvStats(null);
          setUploadProgress(0);
          return; // Close modal per requirement
        }
      }
      // L1 Approval (no file upload)
      if (approvalState && approvalState.l1Payload && !approvalState.l1ApprovedBy) {
        const res = await subscriptionAPI.approveAllocationUpload(selectedBond.id, { level: 'L1' });
        if (res.data.success) {
          setApprovalStates(s => ({ ...s, [selectedBond.id]: res.data.data }));
          showNotice('success', 'L1 Approved', 'Allocation L1 approval recorded. A different user must Approve L2.');
          // Close modal & reset transient state after L1 approval (same behavior as staging)
          setShowUploadModal(false);
          setShowPreview(false);
          setSelectedBond(null);
          setUploadedFile(null);
          setAllocations([]);
          setCsvErrors([]);
          setCsvWarnings([]);
          setCsvStats(null);
          setUploadProgress(0);
          return; // Exit after closing modal
        }
      }
      // L2 Approval
      if (pendingL2 && !approved) {
        if (approvalState?.l1ApprovedBy === authUser?._id) {
          showNotice('warning', 'Action Blocked', 'You performed L1. A different user must perform L2.');
          return;
        }
        const res = await subscriptionAPI.approveAllocationUpload(selectedBond.id, { level: 'L2' });
        if (res.data.success) {
          setApprovalStates(s => ({ ...s, [selectedBond.id]: res.data.data }));
          showNotice('success', 'L2 Approved', 'Allocation finalized after L2 approval.');
          fetchAllocationBonds();
          // Close modal & reset transient state after L2 approval
          setShowUploadModal(false);
          setShowPreview(false);
          setSelectedBond(null);
          setUploadedFile(null);
          setAllocations([]);
          setCsvErrors([]);
          setCsvWarnings([]);
          setCsvStats(null);
          setUploadProgress(0);
          return; // Exit after closing
        }
      }
      if (approved) {
        setShowUploadModal(false);
      }
    } catch (e) {
      console.error('Allocation approval workflow error:', e);
      showNotice('error', 'Workflow Error', e.response?.data?.message || e.message);
    } finally {
      setIsUploading(false);
    }
  };



  const getStatusBadge = (status) => {
    const statusConfig = {
      'Pending Review': { color: 'yellow', icon: Clock },
      'Ready for Allocation': { color: 'warning', icon: AlertTriangle },
      'Allocation Uploaded': { color: 'success', icon: CheckCircle },
      'Pending L1': { color: 'blue', icon: Clock },
      'Pending L2': { color: 'indigo', icon: Clock },
      'Approved': { color: 'green', icon: CheckCircle },
    };
    const config = statusConfig[status] || { color: 'secondary', icon: FileText };
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-${config.color}-100 text-${config.color}-800`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  // Update bond status based on allocation context and approval workflow
  const getBondStatus = (bond) => {
    // First, use the actual bond status from the database (e.g., "Pending Review")
    if (bond.status && bond.status !== 'Ready for Allocation') {
      return bond.status;
    }

    const approvalState = approvalStates[bond.id];

    // If no approval state exists, use bond status or default to ready for allocation
    if (!approvalState) {
      return bond.status || 'Ready for Allocation';
    }

    // If approval state exists but no l1Payload, it means CSV was uploaded but not staged properly
    if (approvalState.status === 'Pending L1' && !approvalState.l1Payload) {
      return bond.status || 'Ready for Allocation';
    }

    // Use approval workflow status for allocation-specific states
    // But preserve original bond status if it's "Pending Review"
    if (bond.status === 'Pending Review') {
      return 'Pending Review';
    }

    return approvalState.status; // "Pending L1", "Pending L2", or "Approved"
  };

  // Get LOC approval status for a bond (using LOC approval table data)
  const getLocApprovalStatus = (bondId) => {
    const bondData = bondLocSummary[bondId];
    if (!bondData) return 'Loading';

    const directInvestors = bondData.directInvestors || [];
    const custodians = bondData.custodians || [];

    // Collect all LOC approvals
    const allLocApprovals = [];

    // Add direct investor LOC approvals
    directInvestors.forEach(inv => {
      if (inv.locApproval) allLocApprovals.push(inv.locApproval);
    });

    // Add custodian LOC approvals
    custodians.forEach(cust => {
      if (cust.locApprovals) {
        allLocApprovals.push(...cust.locApprovals);
      }
    });

    if (allLocApprovals.length === 0) return 'No Requests';

    // Count approval states
    let pendingCount = 0;
    let l1ApprovedCount = 0;
    let l2ApprovedCount = 0;
    let rejectedCount = 0;
    let processingCount = 0;

    allLocApprovals.forEach(approval => {
      if (approval.rejectedBy) {
        rejectedCount++;
      } else if (approval.processingL2) {
        processingCount++;
      } else if (approval.l2ApprovedBy) {
        l2ApprovedCount++;
      } else if (approval.l1ApprovedBy) {
        l1ApprovedCount++;
      } else {
        pendingCount++;
      }
    });

    // Determine overall status
    if (processingCount > 0) return 'Processing';
    if (rejectedCount > 0) return 'Has Rejections';
    if (l2ApprovedCount === allLocApprovals.length) return 'All Approved';
    if (l1ApprovedCount + l2ApprovedCount === allLocApprovals.length) return 'L1 Complete';
    if (l1ApprovedCount > 0) return 'Partial L1';
    return 'Pending';
  };

  // Functions removed - LOC requests now shown inline in bond cards
  // const handleViewLoc = async (bond) => { ... }
  // const refreshLocData = async () => { ... }
  // const handleViewSubmissionDetails = async (investor, type = 'direct') => { ... }
  // const handleCloseLocModal = () => { ... }
  // const handleCloseSubmissionDetailsModal = () => { ... }

  const getSubmissionIdentifiers = (submission = {}) => {
    // Try _id first (for new submissions), then fall back to locFileId (for existing submissions without _id)
    const submissionId =
      submission._id ||
      submission.id ||
      submission.submissionId ||
      submission.locFileId; // Fallback to locFileId for existing submissions
    const requestId =
      submission.requestId ||
      submission.request_id ||
      submission.request?.id ||
      submission.request ||
      submission.allocationRequestId;
    return { submissionId, requestId };
  };

  const handleStartEditSubmission = (submission) => {
    const { submissionId, requestId } = getSubmissionIdentifiers(submission);
    if (!submissionId || !requestId) {
      showNotice('warning', 'Unavailable', 'Unable to edit this submission.');
      return;
    }

    // Prevent editing if LOC request is already approved
    const approval = locApprovalStates[requestId];
    const isApproved = approval && (
      approval.locApprovalStatus === 'completed' ||
      approval.locApprovalStatus === 'l2_approved' ||
      (approval.l1ApprovedBy && approval.l2ApprovedBy)
    );
    if (isApproved) {
      showNotice('warning', 'Cannot Edit', 'This LOC request has already been approved and cannot be edited.');
      return;
    }

    setEditingSubmissionId(submissionId);
    setEditingRequestId(requestId);
    const amountStr = typeof submission.amount === 'number' ? submission.amount.toString() : '';
    setEditingAmount(amountStr);
    // Store the current amount for comparison to detect if changed
    setOriginalEditAmount(amountStr);
  };

  const handleCancelEditSubmission = () => {
    setEditingSubmissionId(null);
    setEditingRequestId(null);
    setEditingAmount('');
    setOriginalEditAmount('');
    setSubmissionSaving(false);
  };

  const handleSaveSubmissionAmount = async () => {
    if (!editingSubmissionId || !editingRequestId) {
      showNotice('warning', 'Missing Data', 'Submission reference not found.');
      return;
    }
    const numericAmount = Number(editingAmount);
    if (Number.isNaN(numericAmount) || numericAmount <= 0) {
      showNotice('warning', 'Invalid Amount', 'Please enter a positive amount.');
      return;
    }

    // If amount hasn't changed, just cancel the edit
    if (editingAmount === originalEditAmount) {
      handleCancelEditSubmission();
      return;
    }

    // Prevent adjusted amount from exceeding original amount
    const approval = locApprovalStates[editingRequestId];
    if (!approval) {
      showNotice('warning', 'Invalid Request', 'Could not find approval data.');
      return;
    }

    // Use originalRequestedAmount from backend, fallback to totalRequestedAmount if not set yet
    const originalAmount = approval.originalRequestedAmount || approval.totalRequestedAmount;
    if (numericAmount > originalAmount) {
      showNotice('warning', 'Invalid Amount', `Adjusted amount cannot exceed original amount of ${formatCurrency(originalAmount)}`);
      return;
    }

    try {
      setSubmissionSaving(true);
      // Use LOC approval API to update amount
      await locApprovalAPI.updateAmount(
        editingRequestId, // This is the LOC approval ID
        numericAmount
      );
      showNotice(
        'success',
        'Amount Updated',
        'LOC amount updated successfully.'
      );
      // Refresh LOC data for all bonds
      const bondIds = allocationBonds
        .map(bond => bond.id)
        .filter(id => id && id !== 'null' && id !== null);

      // Re-fetch LOC approvals for all bonds
      const locApprovalPromises = bondIds.map(bondId =>
        locApprovalAPI.getApprovalsByBond(bondId).catch(err => {
          console.error(`Error fetching LOC approvals for bond ${bondId}:`, err);
          return { data: { success: false, data: [] } };
        })
      );

      const locApprovalResponses = await Promise.all(locApprovalPromises);
      const bondDataMap = {};
      const locApprovalMap = {};

      bondIds.forEach((bondId, index) => {
        const response = locApprovalResponses[index];
        if (!response.data.success) {
          bondDataMap[bondId] = { directInvestors: [], custodians: [] };
          return;
        }

        const locApprovals = response.data.data || [];
        const directInvestors = [];
        const custodiansMap = {};

        locApprovals.forEach(approval => {
          locApprovalMap[approval._id] = approval;

          if (approval.custodianId) {
            const custodianId = approval.custodianId._id || approval.custodianId;
            if (!custodiansMap[custodianId]) {
              custodiansMap[custodianId] = {
                id: custodianId,
                name: approval.custodianId.corporateInfo?.companyName ||
                  approval.custodianId.corporateInfo?.legalEntityName ||
                  'Unknown Custodian',
                locApprovals: [],
                submissions: [],
                totalAmount: 0
              };
            }
            custodiansMap[custodianId].locApprovals.push(approval);
            // Add formatted submission for UI display
            custodiansMap[custodianId].submissions.push({
              _id: approval._id,
              id: approval._id,
              requestId: approval._id,
              investorName: approval.investorId?.corporateInfo?.companyName ||
                approval.investorId?.corporateInfo?.legalEntityName ||
                approval.investorId?.personalInfo?.fullName ||
                [approval.investorId?.personalInfo?.firstName, approval.investorId?.personalInfo?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                'Unknown Sub-Investor',
              investorEmail: approval.investorId?.email || '',
              amount: approval.totalRequestedAmount || 0,
              locApproval: approval
            });
            custodiansMap[custodianId].totalAmount += approval.totalRequestedAmount || 0;
          } else {
            directInvestors.push({
              id: approval._id,
              investorId: approval.investorId._id || approval.investorId,
              name: approval.investorId.corporateInfo?.companyName ||
                approval.investorId.corporateInfo?.legalEntityName ||
                approval.investorId.personalInfo?.fullName ||
                [approval.investorId.personalInfo?.firstName, approval.investorId.personalInfo?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                'Unknown Investor',
              email: approval.investorId.email || '',
              totalAmount: approval.totalRequestedAmount || 0,
              locApproval: approval
            });
          }
        });

        bondDataMap[bondId] = {
          directInvestors,
          custodians: Object.values(custodiansMap)
        };
      });

      setBondLocSummary(bondDataMap);
      setLocApprovalStates(locApprovalMap);

      handleCancelEditSubmission();
    } catch (error) {
      console.error('Error updating LOC amount:', error);
      showNotice(
        'error',
        'Update Failed',
        error.response?.data?.message || 'Failed to update LOC amount'
      );
    } finally {
      setSubmissionSaving(false);
    }
  };

  const handleRejectLOC = async (locApprovalId) => {
    if (!locApprovalId) {
      showNotice('error', 'Error', 'Invalid LOC approval');
      return;
    }

    const confirmMessage = 'Are you sure you want to reject this LOC request?';

    setConfirmModal({
      open: true,
      title: 'Reject LOC Request',
      message: confirmMessage,
      confirmLabel: 'Reject',
      showInput: true,
      inputLabel: 'Reason for rejection (required)',
      inputPlaceholder: 'Enter rejection reason...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (reason) => {
        if (!reason || !reason.trim()) {
          showNotice('error', 'Rejection Failed', 'Rejection reason is required');
          return;
        }

        setConfirmModal({ ...confirmModal, open: false, inputValue: '' });
        setApprovingRequests(prev => new Set([...prev, locApprovalId]));

        try {
          showNotice('info', 'Processing', 'Submitting rejection request...');

          const response = await locApprovalAPI.reject(locApprovalId, reason.trim());

          if (response.jobId) {
            // Job-based rejection - start polling
            showNotice('info', 'Processing', 'Rejection job started. Processing in background...');

            startJobPolling(response.jobId, async (jobResult) => {
              setApprovingRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(locApprovalId);
                return newSet;
              });

              if (jobResult.status === 'completed') {
                showNotice('success', 'Success', 'LOC request rejected successfully');
                await refreshLocData(true, true);
              } else if (jobResult.status === 'failed') {
                const errorMsg = jobResult.error || 'Rejection failed';
                showNotice('error', 'Rejection Failed', errorMsg);
                await refreshLocData(true, true);
              }
            });
          } else if (response.data.success) {
            // Immediate response (fallback for backwards compatibility)
            showNotice('success', 'Rejection Successful', 'LOC request has been rejected');
            setApprovingRequests(prev => {
              const newSet = new Set(prev);
              newSet.delete(locApprovalId);
              return newSet;
            });
            await refreshLocData(true, true);
          } else {
            showNotice('error', 'Rejection Failed', response.data.message || 'Failed to reject request');
            setApprovingRequests(prev => {
              const newSet = new Set(prev);
              newSet.delete(locApprovalId);
              return newSet;
            });
          }
        } catch (error) {
          console.error('Rejection error:', error);
          showNotice(
            'error',
            'Rejection Failed',
            error.response?.data?.message || error.message || 'Failed to reject LOC request'
          );
          setApprovingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(locApprovalId);
            return newSet;
          });
        }
      }
    });
  };

  const handleApproveLOC = async (locApprovalId, level) => {
    if (!locApprovalId) {
      showNotice('error', 'Error', 'Invalid LOC approval');
      return;
    }

    const confirmMessage = level === 'L1'
      ? 'Are you sure you want to approve this LOC request at Level 1?'
      : 'Are you sure you want to approve this LOC request at Level 2? This will perform tracking transfers.';

    setConfirmModal({
      open: true,
      title: `${level} Approval`,
      message: confirmMessage,
      confirmLabel: `Approve ${level}`,
      showInput: true,
      inputLabel: `Optional notes for ${level} approval`,
      inputPlaceholder: 'Enter notes (optional)...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (notes) => {

        setConfirmModal({ ...confirmModal, open: false, inputValue: '' });
        setApprovingRequests(prev => new Set([...prev, locApprovalId]));

        try {
          if (level === 'L2') {
            // Individual L2 approval now uses job-based processing
            showNotice('info', 'Processing', `Submitting L2 approval request...`);

            const response = await locApprovalAPI.approveL2(locApprovalId, notes || '');
            const jobId = response.data.data?.jobId;

            if (jobId) {
              showNotice('info', 'Processing', `L2 approval job started. Processing in background...`);

              // Update LOC approval state to show processing
              setLocApprovalStates(prev => ({
                ...prev,
                [locApprovalId]: {
                  ...prev[locApprovalId],
                  processingL2: true,
                  locApprovalStatus: 'processing_l2'
                }
              }));

              startJobPolling(jobId, async (jobResult) => {
                setApprovingRequests(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(locApprovalId);
                  return newSet;
                });

                const successCount = jobResult.results?.successCount || 0;
                const errorCount = jobResult.results?.errorCount || 0;

                if (successCount > 0) {
                  showNotice(
                    'success',
                    'L2 Approval Successful',
                    'LOC approved and tracking transfers completed.'
                  );
                } else {
                  const errorMsg = jobResult.results?.errors?.[0]?.error || 'Approval failed';
                  showNotice('error', 'Approval Failed', errorMsg);
                }

                // Update local state to clear processingL2 flag immediately
                setLocApprovalStates(prev => ({
                  ...prev,
                  [locApprovalId]: {
                    ...prev[locApprovalId],
                    processingL2: false,
                    locApprovalStatus: successCount > 0 ? 'l2_approved' : prev[locApprovalId]?.locApprovalStatus,
                    l2ApprovedBy: successCount > 0 ? (prev[locApprovalId]?.l2ApprovedBy || true) : prev[locApprovalId]?.l2ApprovedBy,
                    l2ApprovedAt: successCount > 0 ? new Date() : prev[locApprovalId]?.l2ApprovedAt
                  }
                }));

                await refreshLocData();
              });
            } else {
              // Fallback if no job ID
              showNotice('error', 'Approval Failed', 'No job ID returned from server');
              setApprovingRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(locApprovalId);
                return newSet;
              });
              await refreshLocData();
            }
          } else {
            // L1 is immediate (no job)
            showNotice('info', 'Processing', `Submitting L1 approval request...`);

            const response = await locApprovalAPI.approveL1(locApprovalId, notes || '');

            if (response.data.success) {
              showNotice(
                'success',
                'L1 Approval Successful',
                'LOC approved at Level 1. Level 2 approval is now required.'
              );
              setApprovingRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(locApprovalId);
                return newSet;
              });
              await refreshLocData(true, true);
            } else {
              showNotice('error', 'Approval Failed', response.data.message || 'Failed to approve LOC');
              setApprovingRequests(prev => {
                const newSet = new Set(prev);
                newSet.delete(locApprovalId);
                return newSet;
              });
            }
          }
        } catch (error) {
          console.error(`${level} approval error:`, error);
          showNotice(
            'error',
            'Approval Failed',
            error.response?.data?.message || error.message || `Failed to approve LOC at ${level}`
          );
          setApprovingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(locApprovalId);
            return newSet;
          });
        }
      }
    });
  };

  const handleBulkRejectLOC = async (locApprovalIds, entityName) => {
    if (!locApprovalIds || locApprovalIds.length === 0) {
      showNotice('error', 'Error', 'No LOC approvals to reject');
      return;
    }

    // Filter out already-rejected or completed requests
    const pendingLocIds = locApprovalIds.filter(locId => {
      const approval = locApprovalStates[locId];
      if (!approval) return true;

      const isRejected = approval.rejectedBy;
      const isCompleted = approval.l2ApprovedBy || approval.cashTokenReturnedAt;

      return !isRejected && !isCompleted;
    });

    if (pendingLocIds.length === 0) {
      showNotice('info', 'No Action Needed', `All LOC requests for ${entityName} are already processed`);
      return;
    }

    const skippedCount = locApprovalIds.length - pendingLocIds.length;
    const skippedMessage = skippedCount > 0 ? ` (${skippedCount} already processed will be skipped)` : '';

    const confirmMessage = `Are you sure you want to reject ${pendingLocIds.length} LOC requests for ${entityName}?${skippedMessage}`;

    setConfirmModal({
      open: true,
      title: 'Bulk Rejection',
      message: confirmMessage,
      confirmLabel: `Reject ${pendingLocIds.length} Requests`,
      showInput: true,
      inputLabel: 'Reason for bulk rejection (required)',
      inputPlaceholder: 'Enter rejection reason...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (reason) => {
        if (!reason || !reason.trim()) {
          showNotice('error', 'Rejection Failed', 'Rejection reason is required');
          return;
        }

        try {
          setApprovingRequests(prev => new Set([...prev, ...pendingLocIds]));
          setConfirmModal({ ...confirmModal, open: false, inputValue: '' });

          showNotice(
            'info',
            'Bulk Rejection Started',
            `Processing ${pendingLocIds.length} requests...`
          );

          // Process sequentially
          let successCount = 0;
          let errorCount = 0;
          const errors = [];

          for (const locId of pendingLocIds) {
            try {
              await locApprovalAPI.reject(locId, reason.trim());
              successCount++;
            } catch (error) {
              errorCount++;
              errors.push({ locId, error: error.response?.data?.message || error.message });
            }
          }

          // Build detailed error message if there are errors
          let errorDetails = '';
          if (errorCount > 0 && errors.length > 0) {
            errorDetails = '\n\nErrors:\n' + errors.slice(0, 5).map(err =>
              `• LOC ${err.locId}: ${err.error}`
            ).join('\n');
            if (errors.length > 5) {
              errorDetails += `\n• ...and ${errors.length - 5} more errors`;
            }
          }

          showNotice(
            errorCount === 0 ? 'success' : errorCount === pendingLocIds.length ? 'error' : 'warning',
            `Bulk Rejection ${errorCount === 0 ? 'Completed' : errorCount === pendingLocIds.length ? 'Failed' : 'Partially Completed'}`,
            `${successCount} requests rejected successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}${errorDetails}`
          );

          // Refresh LOC data
          const bondIds = allocationBonds
            .map(bond => bond.id)
            .filter(id => id && id !== 'null' && id !== null);

          const locApprovalPromises = bondIds.map(bondId =>
            locApprovalAPI.getApprovalsByBond(bondId).catch(err => {
              console.error(`Error fetching LOC approvals for bond ${bondId}:`, err);
              return { data: { success: false, data: [] } };
            })
          );

          const locApprovalResponses = await Promise.all(locApprovalPromises);
          const bondDataMap = {};
          const locApprovalMap = {};

          bondIds.forEach((bondId, index) => {
            const response = locApprovalResponses[index];
            if (!response.data.success) {
              bondDataMap[bondId] = { directInvestors: [], custodians: [] };
              return;
            }

            const locApprovals = response.data.data || [];
            const directInvestors = [];
            const custodiansMap = {};

            locApprovals.forEach(approval => {
              locApprovalMap[approval._id] = approval;

              if (approval.custodianId) {
                const custodianId = approval.custodianId._id || approval.custodianId;
                if (!custodiansMap[custodianId]) {
                  custodiansMap[custodianId] = {
                    id: custodianId,
                    name: approval.custodianId.corporateInfo?.companyName ||
                      approval.custodianId.corporateInfo?.legalEntityName ||
                      'Unknown Custodian',
                    locApprovals: [],
                    submissions: [],
                    totalAmount: 0
                  };
                }
                custodiansMap[custodianId].locApprovals.push(approval);
                // Add formatted submission for UI display
                custodiansMap[custodianId].submissions.push({
                  _id: approval._id,
                  id: approval._id,
                  requestId: approval._id,
                  investorName: approval.investorId?.corporateInfo?.companyName ||
                    approval.investorId?.corporateInfo?.legalEntityName ||
                    approval.investorId?.personalInfo?.fullName ||
                    [approval.investorId?.personalInfo?.firstName, approval.investorId?.personalInfo?.lastName]
                      .filter(Boolean)
                      .join(' ') ||
                    'Unknown Sub-Investor',
                  investorEmail: approval.investorId?.email || '',
                  amount: approval.totalRequestedAmount || 0,
                  locApproval: approval
                });
                custodiansMap[custodianId].totalAmount += approval.totalRequestedAmount || 0;
              } else {
                directInvestors.push({
                  id: approval._id,
                  investorId: approval.investorId._id || approval.investorId,
                  name: approval.investorId.corporateInfo?.companyName ||
                    approval.investorId.corporateInfo?.legalEntityName ||
                    approval.investorId.personalInfo?.fullName ||
                    [approval.investorId.personalInfo?.firstName, approval.investorId.personalInfo?.lastName].filter(Boolean).join(' ') || 'Unknown Investor', email: approval.investorId.email || '',
                  totalAmount: approval.totalRequestedAmount || 0,
                  locApproval: approval
                });
              }
            });

            bondDataMap[bondId] = {
              directInvestors,
              custodians: Object.values(custodiansMap)
            };
          });

          setBondLocSummary(bondDataMap);
          setLocApprovalStates(locApprovalMap);
          await fetchAllocationBonds();

        } catch (error) {
          console.error('Bulk rejection error:', error);
          showNotice(
            'error',
            'Rejection Failed',
            error.response?.data?.message || error.message || 'Failed to reject LOC requests'
          );
        } finally {
          setApprovingRequests(prev => {
            const next = new Set(prev);
            pendingLocIds.forEach(id => next.delete(id));
            return next;
          });
        }
      }
    });
  };

  const handleBulkApproveLOC = async (locApprovalIds, level, custodianName) => {
    if (!locApprovalIds || locApprovalIds.length === 0) {
      showNotice('error', 'Error', 'No LOC approvals to approve');
      return;
    }

    // Filter out already-approved requests AND same-user/related-user L2 approvals
    const pendingLocIds = locApprovalIds.filter(locId => {
      const approval = locApprovalStates[locId];
      if (!approval) return true; // Include if no approval state yet

      const hasL1Approval = approval.l1ApprovedBy;
      const hasL2Approval = approval.l2ApprovedBy;
      const isProcessing = approval.processingL2;
      const isRejected = approval.rejectedBy;

      if (level === 'L1') {
        return !hasL1Approval && !isRejected;
      } else {
        // For L2, also check that current user didn't approve L1 and not currently processing
        const l1ApproverId = approval.l1ApprovedBy?._id || approval.l1ApprovedBy;
        const currentUserId = authUser?.id || authUser?._id;
        const isSameUserL1 = l1ApproverId && currentUserId && String(l1ApproverId) === String(currentUserId);
        return !hasL2Approval && !isRejected && !isSameUserL1 && !isProcessing && hasL1Approval;
      }
    });

    if (pendingLocIds.length === 0) {
      showNotice('info', 'No Action Needed', `All LOC requests for ${custodianName} are already approved at ${level}`);
      return;
    }

    const skippedCount = locApprovalIds.length - pendingLocIds.length;
    const skippedMessage = skippedCount > 0 ? ` (${skippedCount} already approved will be skipped)` : '';

    const confirmMessage = level === 'L1'
      ? `Are you sure you want to approve ${pendingLocIds.length} LOC requests for ${custodianName} at Level 1?${skippedMessage}`
      : `Are you sure you want to approve ${pendingLocIds.length} LOC requests for ${custodianName} at Level 2? This will perform tracking transfers.${skippedMessage}`;

    setConfirmModal({
      open: true,
      title: `Bulk ${level} Approval`,
      message: confirmMessage,
      confirmLabel: `Approve ${pendingLocIds.length} Requests`,
      showInput: true,
      inputLabel: `Optional notes for bulk ${level} approval`,
      inputPlaceholder: 'Enter notes (optional)...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (notes) => {
        setApprovingRequests(prev => new Set([...prev, ...pendingLocIds]));
        setConfirmModal({ ...confirmModal, open: false, inputValue: '' });

        if (level === 'L2') {
          // Use job-based approval for L2
          try {
            showNotice(
              'info',
              `Bulk L2 Approval Started`,
              `Processing ${pendingLocIds.length} requests in background...`
            );

            const response = await locApprovalAPI.bulkApproveL2(pendingLocIds, notes || '');
            const jobId = response.data.data.jobId;

            // Start polling for job status
            startJobPolling(jobId, async (job) => {
              const { results, status } = job;
              const successCount = results.successCount || 0;
              const errorCount = results.errorCount || 0;

              let errorDetails = '';
              if (errorCount > 0 && results.errors?.length > 0) {
                errorDetails = '\n\nErrors:\n' + results.errors.slice(0, 5).map(err =>
                  `• LOC ${err.requestId}: ${err.error}`
                ).join('\n');
                if (results.errors.length > 5) {
                  errorDetails += `\n• ...and ${results.errors.length - 5} more errors`;
                }
              }

              // Determine notification type based on results, not just status
              const noticeType = successCount > 0 && errorCount === 0 ? 'success' :
                successCount > 0 && errorCount > 0 ? 'warning' : 'error';
              const noticeTitle = successCount > 0 && errorCount === 0 ? 'Bulk L2 Approval Completed' :
                successCount > 0 && errorCount > 0 ? 'Bulk L2 Approval Partially Completed' :
                  'Bulk L2 Approval Failed';

              showNotice(
                noticeType,
                noticeTitle,
                `${successCount} requests approved successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}. Tracking transfers completed for successful approvals.${errorDetails}`
              );

              // Clear loading state
              setApprovingRequests(prev => {
                const next = new Set(prev);
                pendingLocIds.forEach(id => next.delete(id));
                return next;
              });

              // LOC data already refreshed in pollJob before callback
            });
          } catch (error) {
            console.error(`Bulk L2 approval error:`, error);
            showNotice(
              'error',
              'Bulk Approval Failed',
              error.response?.data?.message || error.message || `Failed to approve LOC requests`
            );
            // Clear loading state on error
            setApprovingRequests(prev => {
              const next = new Set(prev);
              pendingLocIds.forEach(id => next.delete(id));
              return next;
            });
          }
        } else {
          // L1 approval - process sequentially (fast, no blockchain operations)
          try {
            showNotice(
              'info',
              `Bulk ${level} Approval Started`,
              `Processing ${pendingLocIds.length} requests...`
            );

            const approvalFn = locApprovalAPI.approveL1;

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            for (const locId of pendingLocIds) {
              try {
                await approvalFn(locId, notes || '');
                successCount++;
              } catch (error) {
                errorCount++;
                errors.push({ locId, error: error.response?.data?.message || error.message });
              }
            }

            // Build detailed error message if there are errors
            let errorDetails = '';
            if (errorCount > 0 && errors.length > 0) {
              errorDetails = '\n\nErrors:\n' + errors.slice(0, 5).map(err =>
                `• LOC ${err.locId}: ${err.error}`
              ).join('\n');
              if (errors.length > 5) {
                errorDetails += `\n• ...and ${errors.length - 5} more errors`;
              }
            }

            showNotice(
              errorCount === 0 ? 'success' : errorCount === pendingLocIds.length ? 'error' : 'warning',
              `Bulk ${level} Approval ${errorCount === 0 ? 'Completed' : errorCount === pendingLocIds.length ? 'Failed' : 'Partially Completed'}`,
              `${successCount} requests approved successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}${errorDetails}`
            );

            // Refresh LOC data
            await refreshLocData(true, true);
          } catch (error) {
            console.error(`Bulk ${level} approval error:`, error);
            showNotice(
              'error',
              'Bulk Approval Failed',
              error.response?.data?.message || error.message || `Failed to approve LOC requests`
            );
          } finally {
            setApprovingRequests(prev => {
              const next = new Set(prev);
              pendingLocIds.forEach(id => next.delete(id));
              return next;
            });
          }
        }
      }
    });
  };

  // Helper function to resume polling for active jobs
  const resumeActiveJobPolling = async (locApprovalMap) => {
    if (!locApprovalMap || typeof locApprovalMap !== 'object') {
      return;
    }

    const processingApprovals = Object.values(locApprovalMap).filter(
      approval => approval && approval.activeJobId && approval.processingL2
    );

    if (processingApprovals.length > 0) {
      console.log(`Found ${processingApprovals.length} potentially active jobs to validate`);

      // Validate each job before resuming polling
      for (const approval of processingApprovals) {
        const jobId = approval.activeJobId;
        const requestId = approval._id;

        // Skip if already polling this job
        if (jobPollingIntervals[jobId]) {
          console.log(`Already polling job ${jobId}, skipping`);
          continue;
        }

        // Validate job exists and is still processing before resuming
        try {
          const response = await jobsAPI.getJobStatus(jobId);
          if (response.data.success) {
            const job = response.data.data;

            // Only resume polling if job is actually still in progress
            if (job.status === 'pending' || job.status === 'processing') {
              console.log(`Resuming polling for active job ${jobId}`);

              // Add to approvingRequests to show loading state
              setApprovingRequests(prev => new Set([...prev, requestId]));

              // Start polling for this job
              startJobPolling(jobId, async (job) => {
                const { results, status } = job;
                const successCount = results?.successCount || 0;
                const errorCount = results?.errorCount || 0;

                // Determine notification type based on results
                const noticeType = successCount > 0 && errorCount === 0 ? 'success' :
                  successCount > 0 && errorCount > 0 ? 'warning' : 'error';
                const noticeTitle = successCount > 0 && errorCount === 0 ? 'L2 Approval Completed' :
                  successCount > 0 && errorCount > 0 ? 'L2 Approval Partially Completed' :
                    'L2 Approval Failed';

                showNotice(
                  noticeType,
                  noticeTitle,
                  `${successCount} requests approved successfully${errorCount > 0 ? `, ${errorCount} failed` : ''}.`
                );

                // Clear loading state
                setApprovingRequests(prev => {
                  const newSet = new Set(prev);
                  newSet.delete(requestId);
                  return newSet;
                });

                // LOC data already refreshed in pollJob before callback
              });
            } else {
              console.log(`Job ${jobId} is ${job.status}, not resuming polling - will clean up and refresh LOC data`);

              // Clean up the completed job from activeJobs
              setActiveJobs(prev => {
                const next = { ...prev };
                delete next[jobId];
                return next;
              });

              // Job is completed, just refresh LOC data to update UI
              await refreshLocData(true, true);
            }
          }
        } catch (error) {
          // Job doesn't exist or we don't have access - skip this job and clean it up
          console.warn(`Cannot access job ${jobId}, cleaning up:`, error.message);

          // Remove from active jobs
          setActiveJobs(prev => {
            const next = { ...prev };
            delete next[jobId];
            return next;
          });

          // Clear loading state for this request
          setApprovingRequests(prev => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
          });

          // Don't throw or cause logout - just continue with other jobs
        }
      }
    }
  };

  // Helper function to refresh LOC data with debouncing
  const refreshLocData = async (immediate = false, force = false) => {
    // Clear existing timeout if immediate refresh is requested
    if (immediate && refreshTimeout) {
      clearTimeout(refreshTimeout);
      setRefreshTimeout(null);
    }

    // If not immediate, debounce the refresh to batch multiple calls
    if (!immediate) {
      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
      const timeout = setTimeout(() => {
        performRefresh(force);
        setRefreshTimeout(null);
      }, 500); // Reduced to 500ms for faster updates
      setRefreshTimeout(timeout);
      return;
    }

    // Perform immediate refresh
    await performRefresh(force);
  };

  const performRefresh = async (force = false) => {
    // Check cache - skip if we fetched recently (unless forced)
    const now = Date.now();
    if (!force && now - lastFetchTime < CACHE_DURATION) {
      console.log('Skipping LOC data fetch - using cached data');
      return;
    }
    setLastFetchTime(now);

    const bondIds = allocationBonds
      .map(bond => bond.id)
      .filter(id => id && id !== 'null' && id !== null);

    const locApprovalPromises = bondIds.map(bondId =>
      locApprovalAPI.getApprovalsByBond(bondId).catch(err => {
        console.error(`Error fetching LOC approvals for bond ${bondId}:`, err);
        return { data: { success: false, data: [] } };
      })
    );

    const locApprovalResponses = await Promise.all(locApprovalPromises);
    const bondDataMap = {};
    const locApprovalMap = {};

    bondIds.forEach((bondId, index) => {
      const response = locApprovalResponses[index];
      if (!response.data.success) {
        bondDataMap[bondId] = { directInvestors: [], custodians: [] };
        return;
      }

      const locApprovals = response.data.data || [];
      const directInvestors = [];
      const custodiansMap = {};

      locApprovals.forEach(approval => {
        // Store the full approval object with all fields
        locApprovalMap[approval._id] = {
          ...approval,
          // Ensure processingL2 flag is preserved from backend
          processingL2: approval.processingL2 || false,
          // Map locApprovalStatus based on approval state
          locApprovalStatus: approval.l2ApprovedBy ? 'l2_approved' :
            approval.processingL2 ? 'processing_l2' :
              approval.l1ApprovedBy ? 'l1_approved' :
                approval.rejectedBy ? 'rejected' : 'pending'
        };

        if (approval.custodianId) {
          const custodianId = approval.custodianId._id || approval.custodianId;
          if (!custodiansMap[custodianId]) {
            custodiansMap[custodianId] = {
              id: custodianId,
              name: approval.custodianId.corporateInfo?.companyName ||
                approval.custodianId.corporateInfo?.legalEntityName ||
                'Unknown Custodian',
              locApprovals: [],
              submissions: [],
              totalAmount: 0
            };
          }
          custodiansMap[custodianId].locApprovals.push(approval);
          // Add formatted submission for UI display
          custodiansMap[custodianId].submissions.push({
            _id: approval._id,
            id: approval._id,
            requestId: approval._id,
            investorName: approval.investorId?.corporateInfo?.companyName ||
              approval.investorId?.corporateInfo?.legalEntityName ||
              approval.investorId?.personalInfo?.fullName ||
              [approval.investorId?.personalInfo?.firstName, approval.investorId?.personalInfo?.lastName]
                .filter(Boolean)
                .join(' ') ||
              'Unknown Sub-Investor',
            investorEmail: approval.investorId?.email || '',
            amount: approval.totalRequestedAmount || 0,
            locApproval: approval
          });
          custodiansMap[custodianId].totalAmount += approval.totalRequestedAmount || 0;
        } else {
          directInvestors.push({
            id: approval._id,
            investorId: approval.investorId._id || approval.investorId,
            name: approval.investorId.corporateInfo?.companyName ||
              approval.investorId.corporateInfo?.legalEntityName ||
              approval.investorId.personalInfo?.fullName ||
              [approval.investorId.personalInfo?.firstName, approval.investorId.personalInfo?.lastName].filter(Boolean).join(' ') || 'Unknown Investor',
            email: approval.investorId.email || '',
            totalAmount: approval.totalRequestedAmount || 0,
            locApproval: approval
          });
        }
      });

      bondDataMap[bondId] = {
        directInvestors,
        custodians: Object.values(custodiansMap)
      };
    });

    setBondLocSummary(bondDataMap);
    setLocApprovalStates(locApprovalMap);

    // Resume polling for any active jobs
    resumeActiveJobPolling(locApprovalMap);
  };


  const handleBondApproval = async (bond, level) => {
    const locData = bondLocSummary[bond.id];
    if (!locData) {
      showNotice('error', 'Error', 'No LOC data available for this bond');
      return;
    }

    // Collect all LOC approval IDs for this bond
    const allLocApprovalIds = [
      ...(locData.directInvestors || []).map(inv => inv.id),
      ...(locData.custodians || []).flatMap(cust => (cust.locApprovals || []).map(loc => loc._id))
    ].filter(Boolean);

    if (allLocApprovalIds.length === 0) {
      showNotice('error', 'Error', 'No LOC requests found for this bond');
      return;
    }

    // Filter out already-approved requests AND same-user L2 approvals
    const pendingLocIds = allLocApprovalIds.filter(locId => {
      const approval = locApprovalStates[locId];
      if (!approval) return true; // Include if no approval state yet

      const hasL1Approval = approval.l1ApprovedBy;
      const hasL2Approval = approval.l2ApprovedBy;
      const isProcessing = approval.processingL2;
      const isRejected = approval.rejectedBy;

      if (level === 'L1') {
        return !hasL1Approval && !isRejected;
      } else {
        // For L2, also check that current user didn't approve L1 and not currently processing
        const l1ApproverId = approval.l1ApprovedBy?._id || approval.l1ApprovedBy;
        const currentUserId = authUser?.id || authUser?._id;
        const isSameUserL1 = l1ApproverId && currentUserId && String(l1ApproverId) === String(currentUserId);
        return !hasL2Approval && !isRejected && !isSameUserL1 && !isProcessing && hasL1Approval;
      }
    });

    if (pendingLocIds.length === 0) {
      showNotice('info', 'No Action Needed', `All LOC requests for "${bond.bondName}" are already approved at ${level}`);
      return;
    }

    const skippedCount = allLocApprovalIds.length - pendingLocIds.length;
    const skippedMessage = skippedCount > 0 ? ` (${skippedCount} already approved will be skipped)` : '';

    const confirmMessage = level === 'L1'
      ? `Approve ${pendingLocIds.length} LOC requests for "${bond.bondName}" at Level 1?${skippedMessage}`
      : `Approve ${pendingLocIds.length} LOC requests for "${bond.bondName}" at Level 2? This will perform tracking transfers.${skippedMessage}`;

    setConfirmModal({
      open: true,
      title: `Bond ${level} Approval`,
      message: confirmMessage,
      confirmLabel: `Approve ${pendingLocIds.length} Requests`,
      showInput: true,
      inputLabel: `Optional notes for bulk ${level} approval`,
      inputPlaceholder: 'Enter notes (optional)...',
      inputValue: '',
      isTextArea: true,
      onConfirm: async (notes) => {
        setApprovingBonds(prev => new Set([...prev, bond.id]));
        setConfirmModal({ ...confirmModal, open: false, inputValue: '' });

        if (level === 'L2') {
          // Use job-based approval for L2
          try {
            showNotice(
              'info',
              `Bond L2 Approval Started`,
              `Processing ${pendingLocIds.length} requests for "${bond.bondName}" in background...`
            );

            const response = await locApprovalAPI.bulkApproveL2(pendingLocIds, notes || '');
            const jobId = response.data.data.jobId;

            // Start polling for job status
            startJobPolling(jobId, async (job) => {
              const { results, status } = job;
              const successCount = results.successCount || 0;
              const errorCount = results.errorCount || 0;

              let errorDetails = '';
              if (errorCount > 0 && results.errors?.length > 0) {
                errorDetails = '\n\nErrors:\n' + results.errors.slice(0, 5).map(err =>
                  `• LOC ${err.requestId}: ${err.error}`
                ).join('\n');
                if (results.errors.length > 5) {
                  errorDetails += `\n• ...and ${results.errors.length - 5} more errors`;
                }
              }

              // Determine notification type based on results, not just status
              const noticeType = successCount > 0 && errorCount === 0 ? 'success' :
                successCount > 0 && errorCount > 0 ? 'warning' : 'error';
              const noticeTitle = successCount > 0 && errorCount === 0 ? 'Bond L2 Approval Completed' :
                successCount > 0 && errorCount > 0 ? 'Bond L2 Approval Partially Completed' :
                  'Bond L2 Approval Failed';

              showNotice(
                noticeType,
                noticeTitle,
                `${successCount} requests approved${errorCount > 0 ? `, ${errorCount} failed` : ''}. Tracking transfers completed for successful approvals.${errorDetails}`
              );

              // Clear loading state
              setApprovingBonds(prev => {
                const next = new Set(prev);
                next.delete(bond.id);
                return next;
              });

              // LOC data already refreshed in pollJob before callback
            });
          } catch (error) {
            console.error(`Bond L2 approval error:`, error);
            showNotice(
              'error',
              'Bond Approval Failed',
              error.response?.data?.message || error.message || `Failed to approve LOC requests`
            );
            // Clear loading state on error
            setApprovingBonds(prev => {
              const next = new Set(prev);
              next.delete(bond.id);
              return next;
            });
          }
        } else {
          // L1 approval - process sequentially (fast, no blockchain operations)
          try {
            showNotice(
              'info',
              `Bond ${level} Approval Started`,
              `Processing ${pendingLocIds.length} requests for "${bond.bondName}"...`
            );

            const approvalFn = locApprovalAPI.approveL1;

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            for (const locId of pendingLocIds) {
              try {
                await approvalFn(locId, notes || '');
                successCount++;
              } catch (error) {
                errorCount++;
                errors.push({ locId, error: error.response?.data?.message || error.message });
              }
            }

            // Build detailed error message if there are errors
            let errorDetails = '';
            if (errorCount > 0 && errors.length > 0) {
              errorDetails = '\n\nErrors:\n' + errors.slice(0, 5).map(err =>
                `• LOC ${err.locId}: ${err.error}`
              ).join('\n');
              if (errors.length > 5) {
                errorDetails += `\n• ...and ${errors.length - 5} more errors`;
              }
            }

            showNotice(
              errorCount === 0 ? 'success' : errorCount === pendingLocIds.length ? 'error' : 'warning',
              `Bond ${level} Approval ${errorCount === 0 ? 'Completed' : errorCount === pendingLocIds.length ? 'Failed' : 'Partially Completed'}`,
              `${successCount} requests approved${errorCount > 0 ? `, ${errorCount} failed` : ''}.${errorDetails}`
            );

            // Refresh LOC data and bond approval state
            await refreshLocData(true, true);

            // No need to call fetchAllocationBonds separately - it's redundant
          } catch (error) {
            console.error(`Bond ${level} approval error:`, error);
            showNotice(
              'error',
              'Bond Approval Failed',
              error.response?.data?.message || error.message || `Failed to approve LOC requests`
            );
          } finally {
            setApprovingBonds(prev => {
              const next = new Set(prev);
              next.delete(bond.id);
              return next;
            });
          }
        }
      }
    });
  };

  // Modal close functions removed - modals no longer used
  // const handleCloseLocModal = () => { ... }
  // const handleCloseSubmissionDetailsModal = () => { ... }

  // Job polling functionality
  const startJobPolling = (jobId, onComplete) => {
    // Clear any existing interval for this job
    if (jobPollingIntervals[jobId]) {
      clearInterval(jobPollingIntervals[jobId]);
    }

    // Track if callback has been called to prevent duplicate notifications
    let callbackCalled = false;
    let lastProcessedCount = 0;

    const pollJob = async () => {
      // Stop polling if component is unmounted or page is not visible
      if (!isMountedRef.current || !isPageVisibleRef.current) {
        console.log(`Skipping poll for job ${jobId} - component unmounted or page not visible`);
        return;
      }

      try {
        const response = await jobsAPI.getJobStatus(jobId);
        if (response.data.success) {
          const job = response.data.data;

          // Double-check component is still mounted before updating state
          if (!isMountedRef.current) return;

          // Update active jobs state
          setActiveJobs(prev => ({ ...prev, [jobId]: job }));

          // Progressive update: Refresh LOC data if new items have been processed
          const currentProcessedCount = (job.results?.successCount || 0) + (job.results?.errorCount || 0);
          const totalCount = job.metadata?.totalCount || currentProcessedCount;

          if (currentProcessedCount > lastProcessedCount) {
            lastProcessedCount = currentProcessedCount;
            // Refresh LOC data to show updated individual approvals (immediate, force cache bypass)
            refreshLocData(true, true);
          }

          // Check if job is complete
          if (job.status === 'completed' || job.status === 'failed' || job.status === 'partial') {
            // Stop polling immediately to prevent duplicate calls
            if (jobPollingIntervals[jobId]) {
              clearInterval(jobPollingIntervals[jobId]);
              setJobPollingIntervals(prev => {
                const next = { ...prev };
                delete next[jobId];
                return next;
              });
            }

            // Remove from active jobs after a short delay
            setTimeout(() => {
              setActiveJobs(prev => {
                const next = { ...prev };
                delete next[jobId];
                return next;
              });
            }, 2000);

            // Call completion callback only once
            if (onComplete && !callbackCalled) {
              callbackCalled = true;
              // Do immediate final refresh before callback (forced to bypass cache)
              await refreshLocData(true, true);
              onComplete(job);
            }
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
        // Stop polling on any persistent error to prevent repeated failures
        if (error.response?.status === 401 || error.response?.status === 404 || error.code === 'ECONNABORTED') {
          console.warn('Job polling failed with error, stopping polling for job:', jobId, error.message);
          if (jobPollingIntervals[jobId]) {
            clearInterval(jobPollingIntervals[jobId]);
            setJobPollingIntervals(prev => {
              const next = { ...prev };
              delete next[jobId];
              return next;
            });
          }
          // Remove from active jobs
          setActiveJobs(prev => {
            const next = { ...prev };
            delete next[jobId];
            return next;
          });
        }
      }
    };

    // Poll immediately
    pollJob();

    // Then poll every 1 second for faster updates
    const intervalId = setInterval(pollJob, 1000);
    setJobPollingIntervals(prev => ({ ...prev, [jobId]: intervalId }));
  };

  // Handle page visibility changes - pause/resume polling when user switches tabs or navigates
  useEffect(() => {
    const handleVisibilityChange = () => {
      isPageVisibleRef.current = !document.hidden;

      if (document.hidden) {
        console.log('Page hidden - polling will be paused');
        // Stop all polling when page is hidden
        Object.entries(jobPollingIntervals).forEach(([jobId, intervalId]) => {
          console.log(`Clearing interval for job ${jobId} due to page visibility`);
          clearInterval(intervalId);
        });
        setJobPollingIntervals({});
      } else {
        console.log('Page visible again - resuming active jobs if any');
        // When page becomes visible again, check if there are active jobs to resume
        const activeJobIds = Object.keys(activeJobs);
        if (activeJobIds.length > 0) {
          console.log(`Resuming ${activeJobIds.length} active jobs`);
          // Note: The jobs will be checked and resumed by the existing logic
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [jobPollingIntervals, activeJobs]);

  // Cleanup polling intervals and debounce timeout on unmount
  useEffect(() => {
    isMountedRef.current = true;

    return () => {
      console.log('LOCManagement unmounting - cleaning up all polling intervals');
      isMountedRef.current = false;
      isPageVisibleRef.current = false;

      // Clear all polling intervals
      Object.entries(jobPollingIntervals).forEach(([jobId, intervalId]) => {
        console.log(`Clearing interval for job ${jobId}`);
        clearInterval(intervalId);
      });

      if (refreshTimeout) {
        clearTimeout(refreshTimeout);
      }
    };
  }, []);

  // Export PDF Report
  const exportReport = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Title
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.text('LOC Approval Report', pageWidth / 2, 20, { align: 'center' });

    // Report date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    const reportDate = new Date().toLocaleString();
    doc.text(`Generated: ${reportDate}`, pageWidth / 2, 28, { align: 'center' });

    // Collect all LOC approvals from all bonds
    const allLocApprovals = [];

    filteredBonds.forEach((bond) => {
      const bondSummary = bondLocSummary[bond.id];
      if (!bondSummary) return;

      // Add direct investor LOC approvals
      if (bondSummary.directInvestors && bondSummary.directInvestors.length > 0) {
        bondSummary.directInvestors.forEach(investor => {
          const approval = locApprovalStates[investor.id];
          let status = 'Pending';
          let approvedBy = '-';
          let l1ApprovedBy = '-';
          let l2ApprovedBy = '-';
          let createdAt = '-';

          if (approval) {
            // Format createdAt
            if (approval.createdAt) {
              createdAt = formatDate(approval.createdAt);
            }

            // Get L1 approver
            if (approval.l1ApprovedBy) {
              l1ApprovedBy = approval.l1ApprovedBy?.email ||
                approval.l1ApprovedBy?.corporateInfo?.companyName ||
                approval.l1ApprovedBy?.personalInfo?.fullName ||
                'Unknown';
            }

            // Get L2 approver
            if (approval.l2ApprovedBy) {
              l2ApprovedBy = approval.l2ApprovedBy?.email ||
                approval.l2ApprovedBy?.corporateInfo?.companyName ||
                approval.l2ApprovedBy?.personalInfo?.fullName ||
                'Unknown';
            }

            // Determine status
            if (approval.rejectedBy) {
              status = 'Rejected';
              approvedBy = approval.rejectedBy?.email ||
                approval.rejectedBy?.corporateInfo?.companyName ||
                approval.rejectedBy?.personalInfo?.fullName ||
                'Unknown';
            } else if (approval.processingL2 || approval.locApprovalStatus === 'processing_l2') {
              status = 'Processing';
              approvedBy = '-';
            } else if (approval.l2ApprovedBy) {
              status = 'Approved';
              approvedBy = l2ApprovedBy;
            } else if (approval.l1ApprovedBy) {
              status = 'Pending L2';
              approvedBy = '-';
            } else {
              status = 'Pending';
              approvedBy = '-';
            }
          }

          allLocApprovals.push({
            investor: investor.name || 'N/A',
            locAmount: investor.totalAmount || 0,
            status: status,
            bondName: bond.bondName || 'N/A',
            approvedBy: approvedBy,
            createdAt: createdAt,
            l1ApprovedBy: l1ApprovedBy,
            l2ApprovedBy: l2ApprovedBy
          });
        });
      }

      // Add custodian/sub-investor LOC approvals
      if (bondSummary.custodians && bondSummary.custodians.length > 0) {
        bondSummary.custodians.forEach(custodian => {
          if (custodian.locApprovals && custodian.locApprovals.length > 0) {
            custodian.locApprovals.forEach(loc => {
              const approval = locApprovalStates[loc._id];
              let status = 'Pending';
              let approvedBy = '-';
              let l1ApprovedBy = '-';
              let l2ApprovedBy = '-';
              let createdAt = '-';

              if (approval) {
                // Format createdAt
                if (approval.createdAt) {
                  createdAt = formatDate(approval.createdAt);
                }

                // Get L1 approver
                if (approval.l1ApprovedBy) {
                  l1ApprovedBy = approval.l1ApprovedBy?.email ||
                    approval.l1ApprovedBy?.corporateInfo?.companyName ||
                    approval.l1ApprovedBy?.personalInfo?.fullName ||
                    'Unknown';
                }

                // Get L2 approver
                if (approval.l2ApprovedBy) {
                  l2ApprovedBy = approval.l2ApprovedBy?.email ||
                    approval.l2ApprovedBy?.corporateInfo?.companyName ||
                    approval.l2ApprovedBy?.personalInfo?.fullName ||
                    'Unknown';
                }

                // Determine status
                if (approval.rejectedBy) {
                  status = 'Rejected';
                  approvedBy = approval.rejectedBy?.email ||
                    approval.rejectedBy?.corporateInfo?.companyName ||
                    approval.rejectedBy?.personalInfo?.fullName ||
                    'Unknown';
                } else if (approval.processingL2 || approval.locApprovalStatus === 'processing_l2') {
                  status = 'Processing';
                  approvedBy = '-';
                } else if (approval.l2ApprovedBy) {
                  status = 'Approved';
                  approvedBy = l2ApprovedBy;
                } else if (approval.l1ApprovedBy) {
                  status = 'Pending L2';
                  approvedBy = '-';
                } else {
                  status = 'Pending';
                  approvedBy = '-';
                }
              }

              const investorName = loc.investorId?.corporateInfo?.companyName ||
                loc.investorId?.corporateInfo?.legalEntityName ||
                loc.investorId?.personalInfo?.fullName ||
                [loc.investorId?.personalInfo?.firstName, loc.investorId?.personalInfo?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                'Unknown Sub-Investor';

              allLocApprovals.push({
                investor: `${investorName} (via ${custodian.name})`,
                locAmount: loc.totalRequestedAmount || 0,
                status: status,
                bondName: bond.bondName || 'N/A',
                approvedBy: approvedBy,
                createdAt: createdAt,
                l1ApprovedBy: l1ApprovedBy,
                l2ApprovedBy: l2ApprovedBy
              });
            });
          }
        });
      }
    });

    // Summary statistics
    let yPosition = 40;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Summary', 14, yPosition);
    yPosition += 8;

    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text(`Total LOC Approvals: ${allLocApprovals.length}`, 14, yPosition);
    yPosition += 6;

    const totalAmount = allLocApprovals.reduce((sum, loc) => sum + loc.locAmount, 0);
    doc.text(`Total LOC Amount: ${formatCurrency(totalAmount)}`, 14, yPosition);
    yPosition += 10;

    // Generate table rows
    const tableRows = allLocApprovals.map(loc => [
      loc.investor,
      formatCurrency(loc.locAmount),
      loc.status,
      loc.bondName,
      loc.createdAt,
      loc.l1ApprovedBy,
      loc.l2ApprovedBy
    ]);

    // Create table with all LOC approvals
    doc.autoTable({
      startY: yPosition,
      head: [['Investor', 'LOC Amount', 'Status', 'Bond Name', 'Created At', 'L1 Approved By', 'L2 Approved By']],
      body: tableRows,
      theme: 'striped',
      headStyles: { fillColor: [59, 130, 246], fontSize: 8 },
      bodyStyles: { fontSize: 7 },
      margin: { left: 14, right: 14 },
      columnStyles: {
        0: { cellWidth: 35 },  // Investor
        1: { cellWidth: 25, halign: 'right' },  // LOC Amount
        2: { cellWidth: 20 },  // Status
        3: { cellWidth: 35 },  // Bond Name
        4: { cellWidth: 22 },  // Created At
        5: { cellWidth: 27 },  // L1 Approved By
        6: { cellWidth: 27 }   // L2 Approved By
      }
    });

    // Save the PDF
    const fileName = `LOC_Approval_Report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    showNotice('success', 'Report Exported', `PDF report has been downloaded as ${fileName}`);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="flex items-center justify-between py-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">LOC Management</h1>
            <p className="mt-2 text-sm text-gray-600">View and manage Letter of Commitment (LOC) requests submitted by investors</p>
          </div>
          <button
            onClick={exportReport}
            className="btn-primary"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        {/* Action Bar */}
        <div className="mb-6 space-y-4">
          {/* Bonds from Subscription Management notification */}
          {getFinalizeBondsFromSubscriptionManagement().length > 0 && (
            <div key="subscription-notification" className="p-4 border border-green-200 rounded-lg bg-green-50">
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 mr-3 text-green-600" />
                <div className="flex-1">
                  <h3 className="text-sm font-medium text-green-900">
                    New Bonds with Subscription Periods
                  </h3>
                  <p className="mt-1 text-sm text-green-700">
                    {getFinalizeBondsFromSubscriptionManagement().length} bond{getFinalizeBondsFromSubscriptionManagement().length > 1 ? 's' : ''} from Subscription Management have subscription periods set and are ready for LOC review.
                  </p>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    {getFinalizeBondsFromSubscriptionManagement().length} Ready
                  </span>
                </div>
              </div>
            </div>
          )}

          <div key="action-bar" className="px-4 py-4 space-y-4 bg-white">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-lg font-medium text-gray-900">Bonds with LOC Requests</h2>
                <p className="text-sm text-gray-600">View and manage Letter of Commitment requests from investors for bonds with active subscription periods</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <label htmlFor="statusFilter" className="text-sm font-medium text-gray-700 whitespace-nowrap">
                Filter by Status:
              </label>
              <select
                id="statusFilter"
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(1); // Reset to first page when filtering
                }}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white min-w-[180px]"
              >
                <option value="all">All Statuses</option>
                <option value="Pending">Pending</option>
                <option value="Approved">Approved</option>
              </select>
            </div>
          </div>
        </div>

        {/* Empty State or Bonds Cards */}
        {filteredBonds.length === 0 ? (
          <div className="py-12 text-center bg-white rounded-lg shadow">
            {statusFilter !== 'all' ? (
              <>
                <FileText className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No Bonds Match Filter</h3>
                <p className="max-w-md mx-auto mb-6 text-gray-600">
                  No bonds found with status "{statusFilter}". Try selecting a different filter.
                </p>
                <button
                  onClick={() => setStatusFilter('all')}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
                >
                  Clear Filter
                </button>
              </>
            ) : (
              <>
                <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">No Bonds with LOC Requests</h3>
                <p className="max-w-md mx-auto mb-6 text-gray-600">
                  Bonds will appear here automatically after investors submitted their LOC requests.
                </p>                
              </>
            )}
          </div>
        ) : (
          <div className="space-y-6">
            {filteredBonds.map((bond) => {
              // Get LOC data for this bond
              const bondSummary = bondLocSummary[bond.id];
              const totalLocAmount = bondSummary
                ? (bondSummary.directInvestors || []).reduce((sum, inv) => sum + (inv.totalAmount || 0), 0) +
                (bondSummary.custodians || []).reduce((sum, cust) => sum + (cust.totalAmount || 0), 0)
                : null;

              const isLoading = !bondSummary;
              const bondData = bondSummary;

              // Collect all LOC approval IDs for this bond
              const allBondLocApprovalIds = bondData ? [
                ...(bondData.directInvestors || []).map(inv => inv.id), // LOC approval ID
                ...(bondData.custodians || []).flatMap(cust => (cust.locApprovals || []).map(loc => loc._id))
              ] : [];

              // Check if any L1 is pending and if all are L1 approved
              const allL1Approved = allBondLocApprovalIds.length > 0 && allBondLocApprovalIds.every(locId => {
                const approval = locApprovalStates[locId];
                return approval && approval.l1ApprovedBy;
              });
              const allL2Approved = allBondLocApprovalIds.length > 0 && allBondLocApprovalIds.every(locId => {
                const approval = locApprovalStates[locId];
                return approval && approval.l2ApprovedBy;
              });
              const currentUserId = authUser?.id || authUser?._id;
              const hasL1SameUser = allBondLocApprovalIds.some(locId => {
                const approval = locApprovalStates[locId];
                const l1ApproverId = approval?.l1ApprovedBy?._id || approval?.l1ApprovedBy;
                return l1ApproverId && currentUserId && String(l1ApproverId) === String(currentUserId);
              });
              const canBondBulkL1 = allBondLocApprovalIds.length > 0 && !allL1Approved;
              const canBondBulkL2 = allL1Approved && !allL2Approved && !hasL1SameUser;

              return (
                <div key={bond.id} className="overflow-hidden bg-white border border-gray-200 rounded-lg shadow-md">
                  {/* Bond Header */}
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">{bond.bondName}</h3>
                        <div className="flex items-center mt-1 space-x-4 text-sm text-gray-600">
                          <span className="flex items-center">
                            <Users className="w-4 h-4 mr-1" />
                            {bond.issuer}
                          </span>
                          <span className="flex items-center">
                            <FileText className="w-4 h-4 mr-1" />
                            {bond.productType === "Fixed Notes" ? "Conventional" : bond.productType}
                          </span>
                        </div>
                        <div className="flex items-center mt-2 space-x-4 text-xs text-blue-600">
                          <span className="font-mono">ISIN: {bond.isin || '-'}</span>
                          <span className="font-mono">Stock Code: {bond.stockCode || '-'}</span>
                        </div>
                      </div>
                      <div className="flex items-center ml-4 space-x-2">
                        {/* Bulk Approve Buttons for All LOC Requests */}
                        {!isLoading && bondData && allBondLocApprovalIds.length > 0 && (
                          <React.Fragment key={`bulk-actions-${bond.id}`}>
                            {canBondBulkL1 && (
                              <React.Fragment key={`bond-l1-${bond.id}`}>
                                <button
                                  onClick={async () => {
                                    setApprovingBonds(prev => new Set([...prev, bond.id]));
                                    try {
                                      await handleBulkApproveLOC(allBondLocApprovalIds, 'L1', `${bond.bondName} - All Requests`);
                                    } finally {
                                      setApprovingBonds(prev => {
                                        const next = new Set(prev);
                                        next.delete(bond.id);
                                        return next;
                                      });
                                    }
                                  }}

                                  className="px-3 py-1.5 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50 flex items-center space-x-1"
                                >
                                  {approvingBonds.has(bond.id) ? (
                                    <>
                                      <Loader className="w-3 h-3 animate-spin" />
                                      <span>Approving...</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-3 h-3" />
                                      <span>Approve All (L1)</span>
                                    </>
                                  )}
                                </button>
                                <button
                                  onClick={async () => {
                                    setRejectingBonds(prev => new Set([...prev, bond.id]));
                                    try {
                                      await handleBulkRejectLOC(allBondLocApprovalIds, `${bond.bondName} - All Requests`);
                                    } finally {
                                      setRejectingBonds(prev => {
                                        const next = new Set(prev);
                                        next.delete(bond.id);
                                        return next;
                                      });
                                    }
                                  }}

                                  className="px-3 py-1.5 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50 flex items-center space-x-1"
                                >
                                  {rejectingBonds.has(bond.id) ? (
                                    <>
                                      <Loader className="w-3 h-3 animate-spin" />
                                      <span>Rejecting...</span>
                                    </>
                                  ) : (
                                    <>
                                      <XCircle className="w-3 h-3" />
                                      <span>Reject All</span>
                                    </>
                                  )}
                                </button>
                              </React.Fragment>
                            )}
                            {canBondBulkL2 && (
                              <React.Fragment key={`bond-l2-${bond.id}`}>
                                <button
                                  onClick={async () => {
                                    setApprovingBonds(prev => new Set([...prev, bond.id]));
                                    try {
                                      await handleBulkApproveLOC(allBondLocApprovalIds, 'L2', `${bond.bondName} - All Requests`);
                                    } finally {
                                      setApprovingBonds(prev => {
                                        const next = new Set(prev);
                                        next.delete(bond.id);
                                        return next;
                                      });
                                    }
                                  }}

                                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 flex items-center space-x-1"
                                >
                                  {approvingBonds.has(bond.id) ? (
                                    <>
                                      <Loader className="w-3 h-3 animate-spin" />
                                      <span>Approving...</span>
                                    </>
                                  ) : (
                                    <>
                                      <CheckCircle className="w-3 h-3" />
                                      <span>Approve All (L2)</span>
                                    </>
                                  )}
                                </button>
                              </React.Fragment>
                            )}
                          </React.Fragment>
                        )}
                        <div key={`status-${bond.id}`}>
                          {getStatusBadge(getLocApprovalStatus(bond.id))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bond Details Grid */}
                  <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                      <div key={`loc-amount-${bond.id}`}>
                        <p className="mb-1 text-xs text-gray-500">Total LOC Amount</p>
                        {totalLocAmount !== null ? (
                          <p className="text-sm font-semibold text-blue-900">{formatCurrency(totalLocAmount)}</p>
                        ) : (
                          <p className="text-sm text-gray-500">Loading...</p>
                        )}
                      </div>
                      <div key={`profit-rate-${bond.id}`}>
                        <p className="mb-1 text-xs text-gray-500">Profit/Coupon Rate</p>
                        <p className="text-sm font-semibold text-gray-900">
                          {bond.profitRate ? `${bond.profitRate}% p.a.` : `${bond.couponRate}% p.a.`}
                        </p>
                      </div>
                      <div key={`subscription-${bond.id}`}>
                        <p className="mb-1 text-xs text-gray-500">Subscription Period</p>
                        {bond.subscriptionStart && bond.subscriptionEnd ? (
                          <div>
                            <p className="text-sm font-semibold text-gray-900">
                              {formatDate(bond.subscriptionStart)}
                            </p>
                            <p className="text-xs text-gray-500">to {formatDate(bond.subscriptionEnd)}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-gray-500">Not set</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* LOC Requests Table */}
                  {isLoading ? (
                    <div key={`loading-${bond.id}`} className="px-6 py-8 text-center">
                      <Loader className="w-6 h-6 mx-auto mb-2 text-gray-400 animate-spin" />
                      <p className="text-sm text-gray-600">Loading LOC requests...</p>
                    </div>
                  ) : !bondData || ((bondData.directInvestors?.length || 0) === 0 && (bondData.custodians?.length || 0) === 0) ? (
                    <div key={`empty-${bond.id}`} className="px-6 py-8 text-center">
                      <FileText className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                      <p className="text-sm text-gray-600">No LOC requests for this bond</p>
                    </div>
                  ) : (
                    <div key={`content-${bond.id}`} className="px-6 py-4">
                      {/* Direct Investors */}
                      {bondData.directInvestors && bondData.directInvestors.length > 0 && (
                        <div key="direct-investors" className="mb-6">
                          <h4 className="mb-3 text-sm font-semibold text-gray-900">Direct Investors</h4>
                          <div className="overflow-x-auto border border-gray-200 rounded-lg">
                            <table className="min-w-full divide-y divide-gray-200">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Name</th>
                                  <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Email</th>
                                  <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Amount</th>
                                  <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Status</th>
                                  <th className="px-4 py-3 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Actions</th>
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {bondData.directInvestors.map((investor) => {
                                  const requestId = investor.id;
                                  const submissionId = investor.submissions?.[0]?._id || investor.submissions?.[0]?.id || investor.id;
                                  const isEditing = editingSubmissionId === submissionId && editingRequestId === requestId;
                                  const approval = locApprovalStates[requestId];
                                  const isApproved = approval && (
                                    approval.locApprovalStatus === 'completed' ||
                                    approval.locApprovalStatus === 'l2_approved' ||
                                    (approval.l1ApprovedBy && approval.l2ApprovedBy)
                                  );
                                  const isRejected = approval && (approval.rejectedBy || approval.locApprovalStatus === 'rejected' || approval.locApprovalStatus === 'l1_rejected' || approval.locApprovalStatus === 'l2_rejected');
                                  const isL1Approved = approval && approval.l1ApprovedBy && !approval.l2ApprovedBy && !approval.rejectedBy;
                                  const isPending = !approval || (!approval.l1ApprovedBy && !approval.l2ApprovedBy && !isRejected);
                                  const isProcessing = approval && (approval.locApprovalStatus === 'processing_l2' || approval.processingL2 === true);

                                  return (
                                    <tr key={investor.id} className="hover:bg-gray-50">
                                      <td className="px-4 py-3 text-sm text-gray-900">{investor.name || investor.email?.split('@')[0] || '—'}</td>
                                      <td className="px-4 py-3 text-sm text-gray-700">{investor.email || '—'}</td>
                                      <td className="px-4 py-3 text-sm">
                                        {isEditing ? (
                                          <div className="space-y-1">
                                            <input
                                              type="number"
                                              value={editingAmount}
                                              onChange={(e) => setEditingAmount(e.target.value)}
                                              className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                                              disabled={submissionSaving}
                                            />
                                            {approval && (approval.originalRequestedAmount || approval.totalRequestedAmount) && (
                                              <div className="text-xs text-gray-500">
                                                Max: {formatCurrency(approval.originalRequestedAmount || approval.totalRequestedAmount)}
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <span className="font-medium text-gray-900">{formatCurrency(investor.totalAmount)}</span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3">
                                        {isApproved ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                            <CheckCircle className="w-3 h-3 mr-1" />
                                            Approved
                                          </span>
                                        ) : isRejected ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                            <XCircle className="w-3 h-3 mr-1" />
                                            Rejected
                                          </span>
                                        ) : isProcessing ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                            <Loader className="w-3 h-3 mr-1 animate-spin" />
                                            Processing
                                          </span>
                                        ) : isL1Approved ? (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                            <Clock className="w-3 h-3 mr-1" />
                                            L1 Approved
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                            <Clock className="w-3 h-3 mr-1" />
                                            Pending
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-4 py-3">
                                        <div className="flex items-center space-x-2">
                                          {isEditing ? (
                                            <>
                                              <button
                                                onClick={handleSaveSubmissionAmount}
                                                disabled={submissionSaving}
                                                className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                              >
                                                {submissionSaving ? 'Saving...' : 'Save'}
                                              </button>
                                              <button
                                                onClick={handleCancelEditSubmission}
                                                disabled={submissionSaving}
                                                className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                                              >
                                                Cancel
                                              </button>
                                            </>
                                          ) : (
                                            <>
                                              {!isApproved && !isL1Approved && (
                                                <button
                                                  onClick={() => handleStartEditSubmission({
                                                    _id: submissionId,
                                                    requestId: requestId,
                                                    amount: investor.totalAmount
                                                  })}

                                                  className="px-3 py-1 text-xs font-medium text-blue-600 rounded bg-blue-50 hover:bg-blue-100"
                                                >
                                                  <Edit className="w-3 h-3" />
                                                </button>
                                              )}
                                              {(() => {
                                                const hasActiveJob = Object.values(activeJobs).some(job => {
                                                  const jobRequestIds = job.parameters?.allocationRequestIds || [];
                                                  return jobRequestIds.includes(requestId);
                                                });
                                                // Check if this specific LOC approval is being processed
                                                const isLocProcessing = approval?.processingL2 === true;
                                                const isProcessingRequest = approvingRequests.has(requestId);
                                                const canL1 = isPending && !hasActiveJob && !isLocProcessing && !isProcessingRequest;
                                                const l1ApproverId = approval?.l1ApprovedBy?._id || approval?.l1ApprovedBy;
                                                const currentUserId = authUser?.id || authUser?._id;
                                                const sameUserL1 = !!(isL1Approved && currentUserId && String(l1ApproverId) === String(currentUserId));
                                                const canL2 = isL1Approved && !sameUserL1 && !isProcessing && !hasActiveJob && !isLocProcessing && !isProcessingRequest;

                                                return (
                                                  <React.Fragment key={`actions-${requestId}`}>
                                                    {canL1 && (
                                                      <React.Fragment key={`l1-${requestId}`}>
                                                        <button
                                                          onClick={() => handleApproveLOC(requestId, 'L1')}

                                                          className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                                                        >
                                                          <span>Approve L1</span>
                                                        </button>
                                                        <button
                                                          onClick={() => handleRejectLOC(requestId)}

                                                          className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                                                        >
                                                          <span>Reject</span>
                                                        </button>
                                                      </React.Fragment>
                                                    )}
                                                    {canL2 && (
                                                      <React.Fragment key={`l2-${requestId}`}>
                                                        <button
                                                          onClick={() => handleApproveLOC(requestId, 'L2')}

                                                          className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                                        >
                                                          <span>Approve L2</span>
                                                        </button>
                                                      </React.Fragment>
                                                    )}
                                                  </React.Fragment>
                                                );
                                              })()}
                                            </>
                                          )}
                                        </div>
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Custodians and Sub-Investors */}
                      {bondData.custodians && bondData.custodians.length > 0 && (
                        <div key="custodians">
                          <h4 className="mb-3 text-sm font-semibold text-gray-900">Custodians & Sub-Investors</h4>
                          <div className="space-y-4">
                            {bondData.custodians.map((custodian) => {
                              return (
                                <div key={custodian.custodianId || custodian._id} className="overflow-hidden border border-gray-200 rounded-lg">
                                  <div className="px-4 py-3 bg-gray-50">
                                    <div>
                                      <h5 className="text-sm font-semibold text-gray-900">
                                        {custodian.custodianName || custodian.name || custodian.custodianEmail || custodian.email || 'Unknown Custodian'}
                                      </h5>
                                      <p className="mt-1 text-xs text-gray-600">
                                        {custodian.submissions?.length || 0} sub-investor{(custodian.submissions?.length || 0) !== 1 ? 's' : ''} ·
                                        Total: {formatCurrency(custodian.totalAmount || 0)}
                                      </p>
                                    </div>
                                  </div>
                                  <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Sub-Investor</th>
                                          <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Email</th>
                                          <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Amount</th>
                                          <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Status</th>
                                          <th className="px-4 py-2 text-xs font-medium tracking-wider text-left text-gray-500 uppercase">Actions</th>
                                        </tr>
                                      </thead>
                                      <tbody className="bg-white divide-y divide-gray-200">
                                        {(custodian.submissions || []).map((submission, index) => {
                                          const { submissionId, requestId } = getSubmissionIdentifiers(submission);
                                          const isEditing = editingSubmissionId === submissionId && editingRequestId === requestId;
                                          const approval = locApprovalStates[requestId];
                                          const isApproved = approval && (
                                            approval.locApprovalStatus === 'completed' ||
                                            approval.locApprovalStatus === 'l2_approved' ||
                                            (approval.l1ApprovedBy && approval.l2ApprovedBy)
                                          );
                                          const isRejected = approval && (approval.rejectedBy || approval.locApprovalStatus === 'rejected' || approval.locApprovalStatus === 'l1_rejected' || approval.locApprovalStatus === 'l2_rejected');
                                          const isL1Approved = approval && approval.l1ApprovedBy && !approval.l2ApprovedBy && !approval.rejectedBy;
                                          const isPending = !approval || (!approval.l1ApprovedBy && !approval.l2ApprovedBy && !isRejected);
                                          const isProcessing = approval && (approval.locApprovalStatus === 'processing_l2' || approval.processingL2 === true);

                                          // Generate unique key with fallback
                                          const uniqueKey = requestId || submissionId || `submission-${custodian.custodianId || custodian._id}-${index}`;

                                          return (
                                            <tr key={uniqueKey} className="hover:bg-gray-50">
                                              <td className="px-4 py-2 text-sm text-gray-900">
                                                {submission.investorName || submission.subInvestor?.name || submission.name || '—'}
                                              </td>
                                              <td className="px-4 py-2 text-sm text-gray-700">
                                                {submission.investorEmail || submission.subInvestor?.email || submission.email || '—'}
                                              </td>
                                              <td className="px-4 py-2 text-sm">
                                                {isEditing ? (
                                                  <div className="space-y-1">
                                                    <input
                                                      type="number"
                                                      value={editingAmount}
                                                      onChange={(e) => setEditingAmount(e.target.value)}
                                                      className="w-32 px-2 py-1 text-sm border border-gray-300 rounded"
                                                      disabled={submissionSaving}
                                                    />
                                                    {approval && (approval.originalRequestedAmount || approval.totalRequestedAmount) && (
                                                      <div className="text-xs text-gray-500">
                                                        Max: {formatCurrency(approval.originalRequestedAmount || approval.totalRequestedAmount)}
                                                      </div>
                                                    )}
                                                  </div>
                                                ) : (
                                                  <span className="font-medium text-gray-900">{formatCurrency(submission.amount)}</span>
                                                )}
                                              </td>
                                              <td className="px-4 py-2">
                                                {isApproved ? (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                    Approved
                                                  </span>
                                                ) : isRejected ? (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                                    <XCircle className="w-3 h-3 mr-1" />
                                                    Rejected
                                                  </span>
                                                ) : isProcessing ? (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                                                    <Loader className="w-3 h-3 mr-1 animate-spin" />
                                                    Processing
                                                  </span>
                                                ) : isL1Approved ? (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    L1 Approved
                                                  </span>
                                                ) : (
                                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                                                    <Clock className="w-3 h-3 mr-1" />
                                                    Pending
                                                  </span>
                                                )}
                                              </td>
                                              <td className="px-4 py-2">
                                                <div className="flex items-center space-x-2">
                                                  {isEditing ? (
                                                    <>
                                                      <button
                                                        onClick={handleSaveSubmissionAmount}
                                                        disabled={submissionSaving}
                                                        className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700 disabled:opacity-50"
                                                      >
                                                        {submissionSaving ? 'Saving...' : 'Save'}
                                                      </button>
                                                      <button
                                                        onClick={handleCancelEditSubmission}
                                                        disabled={submissionSaving}
                                                        className="px-3 py-1 text-xs font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 disabled:opacity-50"
                                                      >
                                                        Cancel
                                                      </button>
                                                    </>
                                                  ) : (
                                                    <>
                                                      {!isApproved && !isRejected && !isL1Approved && (
                                                        <button
                                                          onClick={() => handleStartEditSubmission(submission)}

                                                          className="px-3 py-1 text-xs font-medium text-blue-600 rounded bg-blue-50 hover:bg-blue-100"
                                                        >
                                                          <Edit className="w-3 h-3" />
                                                        </button>
                                                      )}
                                                      {(() => {
                                                        const hasActiveJob = Object.values(activeJobs).some(job => {
                                                          const jobRequestIds = job.parameters?.allocationRequestIds || [];
                                                          return jobRequestIds.includes(requestId);
                                                        });
                                                        // Check if this specific LOC approval is being processed
                                                        const isLocProcessing = approval?.processingL2 === true;
                                                        const isProcessingRequest = approvingRequests.has(requestId);
                                                        const canL1 = isPending && !hasActiveJob && !isLocProcessing && !isProcessingRequest;
                                                        const l1ApproverId = approval?.l1ApprovedBy?._id || approval?.l1ApprovedBy;
                                                        const currentUserId = authUser?.id || authUser?._id;
                                                        const sameUserL1 = !!(isL1Approved && currentUserId && String(l1ApproverId) === String(currentUserId));
                                                        const canL2 = isL1Approved && !sameUserL1 && !isProcessing && !hasActiveJob && !isLocProcessing && !isProcessingRequest;

                                                        return (
                                                          <React.Fragment key={`cust-actions-${requestId}`}>
                                                            {canL1 && (
                                                              <React.Fragment key={`cust-l1-${requestId}`}>
                                                                <button
                                                                  onClick={() => handleApproveLOC(requestId, 'L1')}

                                                                  className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-green-600 rounded hover:bg-green-700"
                                                                >
                                                                  <span>Approve L1</span>
                                                                </button>
                                                                <button
                                                                  onClick={() => handleRejectLOC(requestId)}

                                                                  className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-red-600 rounded hover:bg-red-700"
                                                                >
                                                                  <span>Reject</span>
                                                                </button>
                                                              </React.Fragment>
                                                            )}
                                                            {canL2 && (
                                                              <React.Fragment key={`cust-l2-${requestId}`}>
                                                                <button
                                                                  onClick={() => handleApproveLOC(requestId, 'L2')}

                                                                  className="flex items-center px-3 py-1 space-x-1 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
                                                                >
                                                                  <span>Approve L2</span>
                                                                </button>
                                                              </React.Fragment>
                                                            )}
                                                          </React.Fragment>
                                                        );
                                                      })()}
                                                    </>
                                                  )}
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination Controls */}
        {filteredBonds.length > 0 && totalPages > 1 && (
          <div className="px-4 py-3 mt-6 bg-white rounded-lg shadow sm:px-6">
            <div className="flex justify-between flex-1 sm:hidden">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:block">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((currentPage - 1) * pageSize) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * pageSize, totalRecords)}</span> of{' '}
                      <span className="font-medium">{totalRecords}</span> bonds
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap">
                      Per page:
                    </label>
                    <select
                      id="pageSize"
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                      className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value={5}>5</option>
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </div>
                </div>
                <div className="flex justify-center">
                  <nav className="relative z-0 inline-flex -space-x-px rounded-md shadow-sm" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Previous</span>
                      <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      // Show first page, last page, current page, and pages around current
                      if (
                        page === 1 ||
                        page === totalPages ||
                        (page >= currentPage - 1 && page <= currentPage + 1)
                      ) {
                        return (
                          <button
                            key={page}
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === page
                              ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                              : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === currentPage - 2 || page === currentPage + 2) {
                        return <span key={page} className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">...</span>;
                      }
                      return null;
                    })}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span className="sr-only">Next</span>
                      <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && selectedBond && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium text-gray-900">
                  Upload Allocation for {selectedBond.bondName}
                </h3>
                <button
                  onClick={() => {
                    setShowUploadModal(false);
                    setShowPreview(false);
                    setUploadedFile(null);
                    setAllocations([]);
                    setCsvErrors([]);
                    setCsvWarnings([]);
                    setCsvStats(null);
                    setUploadProgress(0);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
              {(() => {
                const approvalState = approvalStates[selectedBond.id];
                const pendingL2 = approvalState && approvalState.status === 'Pending L2';
                const approved = approvalState && approvalState.status === 'Approved';
                return (
                  <div className="mb-4">
                    <div className="flex items-center space-x-2 text-xs">
                      <span className={`px-2 py-0.5 rounded font-medium ${pendingL2 ? 'bg-yellow-100 text-yellow-800' : approved ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>{approvalState ? approvalState.status : 'Pending L1'}</span>
                      {approvalState?.l1ApprovedByEmail && (
                        <span className="text-gray-500">L1 by {approvalState.l1ApprovedByEmail === authUser?.email ? 'You' : approvalState.l1ApprovedByEmail}</span>
                      )}
                      {approvalState?.l2ApprovedByEmail && (
                        <span className="text-gray-500">• L2 by {approvalState.l2ApprovedByEmail === authUser?.email ? 'You' : approvalState.l2ApprovedByEmail}</span>
                      )}
                    </div>
                  </div>
                );
              })()}
              {/* Bond Summary */}
              <div className="p-4 mb-6 rounded-lg bg-gray-50">
                <h4 className="mb-3 text-sm font-medium text-gray-900">Bond Summary</h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Bond Name:</span>
                    <div className="font-medium">{selectedBond.bondName}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Issuer:</span>
                    <div className="font-medium">{selectedBond.issuer}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Nominal Amount:</span>
                    <div className="font-medium">{formatCurrency(selectedBond.nominalAmount)}</div>
                  </div>
                  <div>
                    <span className="text-gray-600">Total Subscribed:</span>
                    <div className="font-medium">{formatCurrency(selectedBond.totalSubscribed)}</div>
                  </div>
                </div>
              </div>

              {/* File Upload Section (hidden in Pending L2 / Approved) */}
              {(() => {
                const approvalState = approvalStates[selectedBond.id];
                const pendingL2 = approvalState && approvalState.status === 'Pending L2';
                const approved = approvalState && approvalState.status === 'Approved';
                const stagedPendingL1 = approvalState && approvalState.l1Payload && !approvalState.l1ApprovedBy;
                if (pendingL2 || approved || stagedPendingL1) {
                  return (
                    <div className="p-4 mb-6 bg-white border border-gray-200 rounded-lg">
                      <h4 className="mb-2 text-sm font-medium text-gray-900">{stagedPendingL1 ? 'Staged Allocation (Pending L1)' : 'Proposed Allocation (L1)'}</h4>
                      {approvalState?.l1Payload ? (
                        <div className="grid grid-cols-2 gap-4 text-xs">
                          <div><span className="text-gray-500">{allocationEntityPluralLabel}:</span> <span className="font-medium">{approvalState.l1Payload.allocationsCount}</span></div>
                          <div><span className="text-gray-500">Total Amount:</span> <span className="font-medium">{formatCurrency(approvalState.l1Payload.totalAmount || 0)}</span></div>
                        </div>
                      ) : (
                        <p className="text-xs text-gray-500">No payload details available.</p>
                      )}
                      {stagedPendingL1 && (
                        <div className="p-2 mt-3 text-xs text-blue-700 border border-blue-200 rounded bg-blue-50">
                          Allocation staged. Review and click Approve L1 to proceed.
                        </div>
                      )}
                      {pendingL2 && (
                        <div className="p-2 mt-3 text-xs text-yellow-700 border border-yellow-200 rounded bg-yellow-50">
                          L1 approved. A different user must perform L2 to commit allocations.
                        </div>
                      )}
                      {approved && (
                        <div className="p-2 mt-3 text-xs text-green-700 border border-green-200 rounded bg-green-50">
                          Allocations committed after L2 approval.
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <div className="mb-6">
                    <h4 className="mb-3 text-sm font-medium text-gray-900">Upload Allocation CSV</h4>
                    <div className="p-6 text-center transition-colors border-2 border-gray-300 border-dashed rounded-lg hover:border-gray-400">
                      <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
                      <div className="mb-4 text-sm text-gray-600">
                        <span className="font-medium text-blue-600 cursor-pointer hover:text-blue-500">
                          Click to upload
                        </span>
                        {' '}or drag and drop your CSV file here
                      </div>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={handleFileUpload}
                        className="hidden"
                        id="csv-upload"
                      />
                      <label
                        htmlFor="csv-upload"
                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm cursor-pointer hover:bg-gray-50"
                      >
                        Choose File
                      </label>
                      <p className="mt-2 text-xs text-gray-500">CSV files only, max 5MB</p>

                      {/* CSV Format Instructions */}
                      <div className="p-3 mt-4 border border-blue-200 rounded-md bg-blue-50">
                        <h5 className="mb-2 text-sm font-medium text-blue-800">CSV Format Requirements:</h5>
                        <ul className="space-y-1 text-xs text-blue-700">
                          <li>• Headers: {allocationEntityIdLabel}, {allocationEntityNameLabel}, Bond Amount, LEI</li>
                          <li>• <strong>{allocationEntityIdLabel}:</strong> Must be a valid User ID (24-character MongoDB ObjectId) from the database</li>
                          <li>• <strong>{allocationEntityNameLabel}:</strong> Should match the user's name in the database</li>
                          <li>• <strong>Bond Amount:</strong> Numbers only (no commas), e.g., 10000000</li>
                          <li>• <strong>LEI:</strong> Must be exactly 20 characters and match the user's registered Legal Entity Identifier</li>
                          <li>• <strong>Note:</strong> Allocation percentages are calculated automatically based on Bond Amount</li>
                          <li>• No empty rows or comment lines in the data</li>
                          <li>• <span className="text-orange-600">⚠️ All IDs and LEIs will be validated against the database</span></li>
                        </ul>
                      </div>
                    </div>

                    {/* Upload Progress */}
                    {isUploading && (
                      <div className="mt-4">
                        <div className="flex items-center mb-2">
                          <Loader className="w-4 h-4 mr-2 text-blue-600 animate-spin" />
                          <span className="text-sm text-gray-600">
                            {uploadProgress < 90 ? 'Processing CSV file...' :
                              uploadProgress < 100 ? 'Validating with database...' :
                                'Validation complete'}
                          </span>
                        </div>
                        <div className="w-full h-2 bg-gray-200 rounded-full">
                          <div
                            className="h-2 transition-all duration-300 bg-blue-600 rounded-full"
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        {uploadProgress >= 90 && uploadProgress < 100 && (
                          <div className="mt-1 text-xs text-blue-600">
                            🔍 Validating IDs and LEIs against database...
                          </div>
                        )}
                      </div>
                    )}

                    {/* File Info */}
                    {uploadedFile && !isUploading && (
                      <div className="p-3 mt-4 border border-blue-200 rounded-md bg-blue-50">
                        <div className="flex items-center">
                          <FileText className="w-5 h-5 mr-2 text-blue-600" />
                          <div>
                            <div className="text-sm font-medium text-blue-800">{uploadedFile.name}</div>
                            <div className="text-xs text-blue-600">
                              {(uploadedFile.size / 1024).toFixed(1)} KB • Uploaded successfully
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}

              {/* CSV Errors */}
              {csvErrors.length > 0 && (
                <div className="p-4 mb-6 border border-red-200 rounded-md bg-red-50">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="mb-2 text-sm font-medium text-red-800">
                        {csvErrors.length === 1 ? 'Validation Issue' : `${csvErrors.length} Validation Issues`}
                      </h4>
                      <div className="space-y-1 overflow-y-auto text-xs text-red-700 max-h-32">
                        {csvErrors.map((error, index) => (
                          <div key={index} className="flex items-start">
                            <span className="mr-1">•</span>
                            <span>{error}</span>
                          </div>
                        ))}
                      </div>
                      {showPreview && (
                        <div className="p-2 mt-2 text-xs text-orange-700 border border-orange-200 rounded bg-orange-50">
                          ⚠️ Some records have validation issues but you can still proceed. Please review carefully before uploading.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* CSV Warnings */}
              {csvWarnings.length > 0 && (
                <div className="p-4 mb-6 border border-yellow-200 rounded-md bg-yellow-50">
                  <div className="flex items-start">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="mb-2 text-sm font-medium text-yellow-800">
                        {csvWarnings.length === 1 ? 'Validation Warning' : `${csvWarnings.length} Validation Warnings`}
                      </h4>
                      <div className="space-y-1 overflow-y-auto text-xs text-yellow-700 max-h-32">
                        {csvWarnings.map((warning, index) => (
                          <div key={index} className="flex items-start">
                            <span className="mr-1">⚠️</span>
                            <span>{warning}</span>
                          </div>
                        ))}
                      </div>
                      <div className="p-2 mt-2 text-xs text-yellow-700 bg-yellow-100 border border-yellow-300 rounded">
                        These are warnings that don't prevent upload, but you should review them carefully.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* CSV Stats */}
              {csvStats && csvErrors.length === 0 && (
                <div className="p-4 mb-6 border border-green-200 rounded-md bg-green-50">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                    <div className="flex-1">
                      <h4 className="mb-2 text-sm font-medium text-green-800">CSV Processing Summary</h4>
                      <div className="grid grid-cols-2 gap-4 text-xs text-green-700">
                        <div>Total Rows: {csvStats.totalRows}</div>
                        <div>Valid Rows: {csvStats.validRows}</div>
                        <div>Total Amount: {formatCurrency(csvStats.totalAmount)}</div>
                      </div>
                      {csvStats.serverValidated && (
                        <div className="p-2 mt-2 text-xs text-blue-700 border border-blue-200 rounded bg-blue-50">
                          ✅ <strong>Database Validation:</strong> IDs and LEIs have been validated against the database
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Preview Table */}
              {showPreview && allocations.length > 0 && (
                <div className="mb-6">
                  <h4 className="mb-3 text-sm font-medium text-gray-900">Allocation Preview</h4>
                  <div className="overflow-x-auto border border-gray-200 rounded-md">
                    <table className="table">
                      <thead className="table-header">
                        <tr>
                          <th className="table-header-cell">{allocationEntityIdLabel}</th>
                          <th className="table-header-cell">{allocationEntityNameLabel}</th>
                          <th className="table-header-cell">Bond Amount</th>
                          <th className="table-header-cell">LEI</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {allocations.map((allocation, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.investorId}</td>
                            <td className="px-3 py-2 text-sm text-gray-900">{allocation.investorName}</td>
                            <td className="px-3 py-2 text-sm text-right text-gray-900">{formatCurrency(allocation.tokenAmount)}</td>
                            <td className="px-3 py-2 font-mono text-sm text-gray-500">{allocation.lei}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Uploaded (Staged) Allocations from L1 when viewing for L2 or after approval */}
              {!showPreview && selectedBond && (() => {
                const st = approvalStates[selectedBond.id];
                if (!st || !st.l1Payload) return null;
                const rows = st.l1Payload.allocations || [];
                if (!rows.length) return null;
                return (
                  <div className="mb-6">
                    <h4 className="flex items-center mb-3 text-sm font-medium text-gray-900">
                      Uploaded Allocations <span className="ml-2 text-xs font-normal text-gray-500">(Staged L1 payload{st.status === 'Approved' ? ' - Approved' : ''})</span>
                    </h4>
                    <div className="overflow-x-auto border border-gray-200 rounded-md max-h-72">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">{allocationEntityIdLabel}</th>
                            <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">{allocationEntityNameLabel}</th>
                            <th className="px-3 py-2 text-xs font-medium text-right text-gray-500 uppercase">Bond Amount</th>
                            <th className="px-3 py-2 text-xs font-medium text-left text-gray-500 uppercase">LEI</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {rows.map((r, i) => (
                            <tr key={i} className="hover:bg-gray-50">
                              <td className="px-3 py-2 text-sm text-gray-900">{r.investorId}</td>
                              <td className="px-3 py-2 text-sm text-gray-900">{r.investorName}</td>
                              <td className="px-3 py-2 text-sm text-right text-gray-900">{formatCurrency(r.tokenAmount)}</td>
                              <td className="px-3 py-2 font-mono text-sm text-gray-500">{r.lei}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="mt-2 text-xs text-gray-500">{rows.length} investors • Total Amount {formatCurrency(st.l1Payload.totalAmount)}</div>
                  </div>
                );
              })()}
            </div>

            <div className="flex justify-between px-6 py-4 border-t border-gray-200">
              {(() => {
                const approvalState = approvalStates[selectedBond.id];
                const pendingL2 = approvalState && approvalState.status === 'Pending L2';
                const approved = approvalState && approvalState.status === 'Approved';
                const staged = approvalState && approvalState.l1Payload && !approvalState.l1ApprovedBy; // staged but not L1 approved
                const isSameUserL1 = pendingL2 && approvalState?.l1ApprovedBy === authUser?._id;
                let adjustedLabel;
                if (approved) adjustedLabel = 'Approved';
                else if (pendingL2) adjustedLabel = 'Approve L2';
                else if (staged) adjustedLabel = 'Approve L1';
                else adjustedLabel = csvWarnings.length > 0 ? 'Stage Upload (With Warnings)' : 'Stage Upload';
                // Allow staging when status is 'Ready for Allocation' (approvalState may exist but without l1Payload)
                const readyForAllocationState = approvalState && approvalState.status === 'Ready for Allocation' && !approvalState.l1Payload;
                // Determine if we are in initial staging mode (no staged payload yet)
                const stagingMode = !staged && !pendingL2 && !approved;
                const hasValidAllocations = allocations.length > 0 && csvErrors.length === 0;
                const disableAction = approved || isSameUserL1 || (stagingMode && !hasValidAllocations);
                return (
                  <>
                    <div className="flex space-x-3">
                      <button
                        onClick={() => {
                          setShowUploadModal(false);
                          setShowPreview(false);
                          setUploadedFile(null);
                          setAllocations([]);
                          setCsvErrors([]);
                          setCsvWarnings([]);
                          setCsvStats(null);
                          setUploadProgress(0);
                        }}
                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUploadConfirm}
                        disabled={disableAction}
                        className={`inline-flex items-center px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md disabled:opacity-50 disabled:cursor-not-allowed ${pendingL2 ? 'bg-green-600 hover:bg-green-700' : approved ? 'bg-gray-400 cursor-default' : staged ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {adjustedLabel}
                      </button>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Notification Toast & styles (assuming they appear below in original file) */}
      {notice.open && (
        <div className="fixed z-50 overflow-hidden bg-white border border-gray-200 rounded-lg shadow-lg top-4 right-4 w-80 animate-fade-in">
          <div className={`px-4 py-3 flex items-start space-x-3 ${notice.type === 'success' ? 'bg-green-50' : notice.type === 'error' ? 'bg-red-50' : notice.type === 'warning' ? 'bg-yellow-50' : 'bg-blue-50'}`}>
            <div className="mt-0.5">
              {notice.type === 'success' && <CheckCircle className="w-5 h-5 text-green-500" />}
              {notice.type === 'error' && <AlertTriangle className="w-5 h-5 text-red-500" />}
              {notice.type === 'warning' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
              {notice.type === 'info' && <FileText className="w-5 h-5 text-blue-500" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-semibold leading-snug text-gray-900">{notice.title}</p>
              <p className="mt-0.5 text-xs text-gray-700 leading-snug whitespace-pre-line">{notice.message}</p>
            </div>
            <button onClick={closeNotice} className="text-gray-400 hover:text-gray-600" aria-label="Close notification">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="relative w-full h-1 bg-gray-100">
            <div className={`absolute inset-y-0 left-0 ${notice.type === 'success' ? 'bg-green-500' : notice.type === 'error' ? 'bg-red-500' : notice.type === 'warning' ? 'bg-yellow-500' : 'bg-blue-500'} animate-notice-progress`}></div>
          </div>
        </div>
      )}

      {/* LOC modals removed - LOC requests now shown inline in bond cards */}

      {/* Confirmation Modal */}
      {confirmModal.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="w-full max-w-md mx-4 bg-white rounded-lg shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">{confirmModal.title}</h3>
            </div>
            <div className="px-6 py-4">
              <p className="mb-4 text-sm text-gray-700">{confirmModal.message}</p>
              {confirmModal.showInput && (
                <div className="mb-4">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    {confirmModal.inputLabel}
                  </label>
                  {confirmModal.isTextArea ? (
                    <textarea
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      rows="3"
                      placeholder={confirmModal.inputPlaceholder}
                      value={confirmModal.inputValue}
                      onChange={(e) => setConfirmModal(prev => ({ ...prev, inputValue: e.target.value }))}
                    />
                  ) : (
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder={confirmModal.inputPlaceholder}
                      value={confirmModal.inputValue}
                      onChange={(e) => setConfirmModal(prev => ({ ...prev, inputValue: e.target.value }))}
                    />
                  )}
                </div>
              )}
            </div>
            <div className="flex justify-end px-6 py-4 space-x-3 border-t border-gray-200">
              <button
                onClick={() => setConfirmModal({ ...confirmModal, open: false, inputValue: '' })}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const callback = confirmModal.onConfirm;
                  const value = confirmModal.inputValue;
                  setConfirmModal({ ...confirmModal, open: false, inputValue: '' });
                  if (callback) callback(value);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700"
              >
                {confirmModal.confirmLabel}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Local styles for simple animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(4px);} to { opacity: 1; transform: translateY(0);} }
        .animate-fade-in { animation: fadeIn 0.25s ease-out; }
        @keyframes noticeProgress { from { width: 100%; } to { width: 0%; } }
        .animate-notice-progress { animation: noticeProgress 3s linear forwards; }
      `}</style>
    </div>
  );
};

export default LOCManagement;
